<template>
  <div class="actuals-page-root" :class="{'actuals-filters-collapsed': !results_filters_open}">
    <FiPageRail title="Bookings" :nowrap-actions="cardView">
      <template #actions>
        <span class="actuals-header-actions">
          <b-button
            v-if="!$route.query.booking_id"
            size="sm"
            variant="danger"
            class="actuals-error-summary-btn"
            title="Summarize error categories for the current date range"
            v-on:click.prevent="open_error_summary_modal()"
          >
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span class="actuals-error-summary-count">{{ error_cnt }}</span>
          </b-button>
          <b-button
            v-if="!$route.query.booking_id"
            size="sm"
            variant="primary"
            title="Reprocess actuals for the current date range"
            aria-label="Reprocess actuals for the current date range"
            v-on:click.prevent="reprocess_actuals()"
          >
            <i class="fa fa-refresh" aria-hidden="true"></i>
          </b-button>
          <b-button
            size="sm"
            variant="success"
            title="Download CSV for the current date range"
            aria-label="Download CSV for the current date range"
            v-on:click.prevent="download()"
          >
            <i class="fa fa-download" aria-hidden="true"></i>
          </b-button>
          <actuals-troubleshooter-help source="tbn"/>
        </span>
      </template>
    </FiPageRail>


    <!-- date presets, then the loaded range as two chips that swap themselves for
         their own picker on click, flanked by day-nudge arrows; presets and arrows
         reload on their own, but a hand-picked From/To waits for Search so that
         setting two dates fires one query instead of one per keystroke -->
    <div class="actuals-tbn-toolbar">
      <!-- date search, matching the Alert History filter row: a Range label, the
           preset chips, and a From/To cluster with double arrows shifting by the
           selected window and single arrows by a day. the From/To cluster hides
           behind the Custom chip at every width, opening on its own whenever the
           loaded window is not one of the presets. -->
      <div class="actuals-date-presets" role="group" aria-label="Date range">
        <span class="actuals-range-label">Range</span>
        <button
          v-for="opt in quick_range_options"
          :key="'qr-' + opt.value"
          type="button"
          :class="['actuals-date-chip', {'actuals-date-chip--active': !is_custom_range && quick_range_active_days === opt.value}]"
          :aria-pressed="!is_custom_range && quick_range_active_days === opt.value ? 'true' : 'false'"
          :title="opt.title"
          v-on:click.prevent="set_quick_range(opt.value)"
        >{{ opt.label }}</button>
        <button
          type="button"
          class="actuals-date-chip actuals-date-chip--custom"
          :class="{'actuals-date-chip--active': dates_open}"
          :aria-pressed="dates_open ? 'true' : 'false'"
          :aria-expanded="dates_open ? 'true' : 'false'"
          aria-controls="actuals-filter-dates"
          aria-label="Set a specific From / To window"
          title="Set a specific From / To window"
          @click="show_custom_dates = !show_custom_dates"
        >Custom</button>
      </div>
      <div
        id="actuals-filter-dates"
        class="actuals-filter-dates"
        :class="{'actuals-filter-dates--open': dates_open}"
      >
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back one window" v-on:click.prevent="nudge_range_days(-selected_day_count)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="nudge_range_days(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date" :min="date_min_iso" :max="date_max_iso"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date" :min="date_min_iso" :max="date_max_iso"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="nudge_range_days(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward one window" v-on:click.prevent="nudge_range_days(selected_day_count)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
        <b-form-select size="sm" v-model="actuals_filter" :options="actuals_filter_options" class="toolbar-select actuals-range-mode"/>
      </div>
      <!-- the booking search and the base filter earn a permanent slot on this
           line rather than sitting behind a disclosure -->
      <span class="toolbar-group">
        <span class="actuals-range-label">By ID</span>
        <b-form-input size="sm" class="toolbar-input" v-model="booking_search" placeholder="bookingID"/>
      </span>
      <b-form-select size="sm" v-if="show_base_select" v-model="selected_base" :options="base_options" class="toolbar-select toolbar-base"/>
      <b-button
        size="sm"
        class="toolbar-btn"
        :variant="range_pending ? 'primary' : 'secondary'"
        :title="range_pending ? 'Search the picked date range -- the results below are still the previous range' : 'Search'"
        v-on:click.prevent="run_search()"
      >Search</b-button>
    </div>

    <div v-if="!this.$route.query.booking_id && !loading" class="actuals-filter-area">
      <div class="actuals-cm-status-filter-row">
      <div class="actuals-filter-chips actuals-tbn-filter-chips">
      <span class="actuals-status-filter-center-group">
      <span class="actuals-filter-label">Status</span>
      <span
        @click="filter_by_status('success')"
        class="actuals-chip actuals-chip-clickable success"
        :class="{'actuals-chip-selected': selected_status == 'success'}"
      >Success {{success_cnt}}</span>
      <span
        @click="filter_by_status('error')"
        class="actuals-chip actuals-chip-clickable error"
        :class="{'actuals-chip-selected': selected_status == 'error'}"
      >Errors {{error_cnt}}</span>
      <span
        @click="filter_by_status('cancelled')"
        class="actuals-chip actuals-chip-clickable cancelled"
        :class="{'actuals-chip-selected': selected_status == 'cancelled'}"
      >Cancelled {{cancelled_cnt}}</span>
      <span
        @click="filter_by_status('unprocessed')"
        class="actuals-chip actuals-chip-clickable unprocessed"
        :class="{'actuals-chip-selected': selected_status == 'unprocessed'}"
      >Unprocessed {{unprocessed_cnt}}</span>
      <span
        @click="filter_by_status('skipped')"
        class="actuals-chip actuals-chip-clickable skipped"
        :class="{'actuals-chip-selected': selected_status == 'skipped'}"
      >Skipped {{skipped_cnt}}</span>
      <!-- card view: the cards carry their own detail, so the row-narrowing
           controls go entirely rather than collapsing behind a pill -->
      <span v-if="!cardView" class="actuals-filter-sep">|</span>
      <button
        type="button"
        v-if="!cardView && !$route.query.booking_id"
        class="actuals-filter-toggle"
        :aria-expanded="results_filters_open ? 'true' : 'false'"
        title="Show the filters that narrow the loaded results"
        @click="toggle_results_filters()"
      ><i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Narrow Results</span>
        <span v-if="active_filter_count" class="actuals-filter-badge">{{ active_filter_count }}</span>
        <i class="fa" :class="results_filters_open ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>
      <transition name="fi-reveal-row">
      <span v-if="!cardView && results_filters_open" class="actuals-filter-chip-group actuals-booking-filter-group">
        <span class="actuals-filter-label">Filter</span>
        <b-form-input size="sm" class="actuals-booking-filter-input" v-model="booking_filter" placeholder=""/>
        <b-form-select size="sm" v-model="selected_driver" :options="driver_filter_options" class="actuals-resource-filter-select"/>
        <b-form-select size="sm" v-model="selected_vehicle" :options="vehicle_filter_options" class="actuals-resource-filter-select"/>
        <span class="actuals-filter-sep">|</span>
        <span class="actuals-filter-chip-group actuals-trip-duration-filter-group">
          <span class="actuals-filter-label" :title="tripDurationFilterTooltips.group">Trip</span>
          <span
            @click="filter_by_trip_duration('single')"
            class="actuals-chip actuals-chip-clickable actuals-trip-duration-chip trip-single"
            :class="{'actuals-chip-selected': selected_trip_duration == 'single'}"
            :title="tripDurationFilterTooltips.single"
          >Single {{ trip_single_cnt }}</span>
          <span
            @click="filter_by_trip_duration('multiday')"
            class="actuals-chip actuals-chip-clickable actuals-trip-duration-chip trip-multiday"
            :class="{'actuals-chip-selected': selected_trip_duration == 'multiday'}"
            :title="tripDurationFilterTooltips.multiday"
          >Multi {{ trip_multiday_cnt }}</span>
        </span>
        <template v-if="show_booking_type_filters">
          <span class="actuals-filter-sep">|</span>
          <span class="actuals-filter-chip-group actuals-booking-type-filter-group">
            <span class="actuals-filter-label" :title="bookingTypeFilterTooltips.group">Type</span>
            <span
              v-for="opt in booking_type_filter_options"
              :key="opt.value"
              @click="filter_by_booking_type(opt.value)"
              class="actuals-chip actuals-chip-clickable actuals-booking-type-chip"
              :class="[
                'booking-type-' + opt.value.replace('_', '-'),
                {'actuals-chip-selected': selected_booking_type == opt.value}
              ]"
              :title="bookingTypeFilterTooltips[opt.value]"
            >{{ opt.label }} {{ opt.count }}</span>
          </span>
        </template>
        <template v-if="show_overlap_filters">
          <span class="actuals-filter-sep">|</span>
          <span class="actuals-filter-chip-group actuals-overlap-filter-group">
            <span class="actuals-filter-label">Overlaps</span>
            <span
              v-if="driver_overlap_cnt > 0 || vehicle_overlap_cnt > 0"
              v-show="!recheck_overlaps_running"
              @click="recheck_overlaps()"
              class="actuals-chip actuals-chip-clickable overlap-recheck-icon"
              title="Re-split overlapping driver/vehicle time and mileage for this date range"
              aria-label="Re-split overlapping driver/vehicle time and mileage for this date range"
              role="button"
            >OL</span>
            <b-spinner v-if="(driver_overlap_cnt > 0 || vehicle_overlap_cnt > 0) && recheck_overlaps_running" small style="width: 0.9rem; height: 0.9rem;" label="Re-checking..."></b-spinner>
            <span
              v-if="driver_overlap_cnt > 0"
              @click="filter_by_status('driver_overlap')"
              class="actuals-chip actuals-chip-clickable overlap-driver"
              :class="{'actuals-chip-selected': selected_status == 'driver_overlap'}"
              title="Driver actual clock-in/out times overlap another booking"
            >By Driver {{driver_overlap_cnt}}</span>
            <span
              v-if="vehicle_overlap_cnt > 0"
              @click="filter_by_status('vehicle_overlap')"
              class="actuals-chip actuals-chip-clickable overlap-vehicle"
              :class="{'actuals-chip-selected': selected_status == 'vehicle_overlap'}"
              title="Vehicle actual start/finish times overlap another booking"
            >By Vehicle {{vehicle_overlap_cnt}}</span>
            <span
              v-if="overlap_correction_cnt > 0"
              @click="filter_by_status('overlap_correction')"
              class="actuals-chip actuals-chip-clickable overlap-correction"
              :class="{'actuals-chip-selected': selected_status == 'overlap_correction'}"
              title="Bookings with overlap allocator corrections applied"
            >Corrected {{overlap_correction_cnt}}</span>
          </span>
        </template>
      </span>
      </transition>
      </span>
      <span class="actuals-filter-chip-group actuals-totals-group">
        <span class="actuals-filter-label">Totals</span>
        <span
          @click="filter_by_status(null)"
          class="actuals-chip actuals-chip-clickable actuals-chip-total"
          :class="{'actuals-chip-selected': selected_status == null}"
        >{{items_row_count}}/{{items_full.length}}</span>
        <span class="actuals-chip">Hrs {{calc_total_duration}}</span>
        <span class="actuals-chip">Miles {{calc_total_miles}}</span>
        <span class="actuals-chip">Odo {{calc_total_odo_miles}}</span>
      </span>
      </div>
      <div v-if="feedback_audit_mode_enabled" class="actuals-filter-chips actuals-tbn-filter-chips actuals-feedback-filter-row">
        <span class="actuals-filter-chip-group actuals-feedback-filter-group">
          <span class="actuals-filter-label" :title="feedbackFilterTooltips.group">Feedback</span>
          <span
            @click="filter_by_feedback('positive')"
            class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-positive"
            :class="{'actuals-chip-selected': selected_feedback == 'positive'}"
            :title="feedbackFilterTooltips.positive"
          ><i class="fa fa-check" aria-hidden="true"></i> {{ feedback_positive_cnt }}</span>
          <span
            @click="filter_by_feedback('negative')"
            class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-negative"
            :class="{'actuals-chip-selected': selected_feedback == 'negative'}"
            :title="feedbackFilterTooltips.negative"
          ><i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ feedback_negative_cnt }}</span>
          <span
            @click="filter_by_feedback('concern')"
            class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-concern"
            :class="{'actuals-chip-selected': selected_feedback == 'concern'}"
            :title="feedbackFilterTooltips.concern"
          ><i class="fa fa-flag" aria-hidden="true"></i> {{ feedback_concern_cnt }}</span>
          <span
            @click="filter_by_feedback('none')"
            class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-none"
            :class="{'actuals-chip-selected': selected_feedback == 'none'}"
            :title="feedbackFilterTooltips.none"
          ><i class="fa fa-circle-o" aria-hidden="true"></i> {{ feedback_none_cnt }}</span>
          <span
            @click="confirm_clear_feedback_for_date_range"
            class="actuals-chip actuals-chip-clickable actuals-feedback-clear-btn"
            :class="{'actuals-chip-disabled': clearing_feedback}"
            :title="feedbackFilterTooltips.clear"
            role="button"
            aria-label="Clear all feedback for this date range"
          ><i class="fa fa-times" aria-hidden="true"></i></span>
        </span>
      </div>
      </div>
    </div>

      <b-table ref="actualsTbnTable" :key="actuals_table_key" :busy="loading" :sticky-header="actuals_sticky_header" striped bordered table-class="actuals-tbn-table"
               :stacked="cardView"
               :items="items" :fields="table_fields"
               :current-page="currentPage" :tbody-tr-class="get_row_color"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="status_msg" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <div v-else-if="show_overlap_time_highlights(row.item)" style="text-align: left">
            <div v-if="row.item.actual.actualClockInTime || row.item.actual.actualClockOutTime">
              <b>driver:</b>
              <span :class="hos_time_highlight_classes(row.item, 'start', row.item.driver_overlap_highlight_start)">{{ formatActualTimeForStatus(row.item.actual.actualClockInTime) }}</span>
              to
              <span :class="hos_time_highlight_classes(row.item, 'finish', row.item.driver_overlap_highlight_end)">{{ formatActualTimeForStatus(row.item.actual.actualClockOutTime) }}</span>
              <br/>
            </div>
            <div v-if="row.item.actual.actualStartLocationTime || row.item.actual.actualFinishLocationTime">
              <b>vehicle:</b>
              <span :class="{'overlap-time': row.item.vehicle_overlap_highlight_start}">{{ formatActualTimeForStatus(row.item.actual.actualStartLocationTime) }}</span>
              to
              <span :class="{'overlap-time': row.item.vehicle_overlap_highlight_end}">{{ formatActualTimeForStatus(row.item.actual.actualFinishLocationTime) }}</span>
              <br/>
            </div>
            <div v-for="(msg, idx) in status_msgs_without_actual_times(row.item)" :key="'overlap-msg-' + idx" style="text-align: left">
              <error-category-popover v-if="row.item.status === 'error'" :message="msg" source="tbn"/><span v-html="msg"/>
            </div>
          </div>
          <div v-else-if="row.item.status === 'success' && row.item.actual" style="text-align: left">
            <div
              v-for="line in format_success_status_lines(row.item)"
              :key="'success-line-' + line.key"
            >
              <template v-if="line.type === 'range'">
                <b>{{ line.label }}:</b>
                <span v-if="line.label === 'driver'" :class="hos_time_highlight_classes(row.item, 'start', false)">{{ formatActualTimeForStatus(line.start) || '—' }}</span>
                <template v-else>{{ formatActualTimeForStatus(line.start) || '—' }}</template>
                to
                <span v-if="line.label === 'driver'" :class="hos_time_highlight_classes(row.item, 'finish', false)">{{ formatActualTimeForStatus(line.end) || '—' }}</span>
                <template v-else>{{ formatActualTimeForStatus(line.end) || '—' }}</template>
                <br/>
              </template>
              <template v-else><span v-html="line.text"></span><br/></template>
            </div>
          </div>
          <template v-else>
            <div style="text-align: left" v-for="(msg, idx) in (row.item.status_msg || '').split(',')" :key="'status-msg-' + idx">
              <error-category-popover v-if="row.item.status === 'error'" :message="msg" source="tbn"/><span v-html="msg"/>
            </div>
          </template>
        </template>

        <template slot="ID" slot-scope="row">
          <span v-if="!row.item._overlap_group_header" class="actuals-id-cell">
            <a href="#" class="actuals-id-copy" @click.prevent="copy_booking_id(row.item.ID)"
               :title="copied_booking_id === row.item.ID ? 'Copied!' : ('Copy ID: ' + row.item.ID)">
              <i :class="copied_booking_id === row.item.ID ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
            </a>
            <router-link
              target="_blank"
              class="actuals-id-text actuals-id-link"
              :to="booking_search_route(row.item.ID)"
              :title="'Search for ' + row.item.ID"
            >{{ row.item.ID }}</router-link>
          </span>
        </template>

        <template slot="bookingID" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <router-link
            v-else
            target="_blank"
            class="actuals-id-link"
            :to="booking_search_route(row.item.bookingID)"
            :title="'Search for ' + row.item.bookingID"
          >{{ row.item.bookingID }}</router-link>
        </template>

        <template slot="vehicleAssignmentID" slot-scope="row">
          {{row.item.vehicleAssignmentID}}
        </template>

        <template slot="dispatchID" slot-scope="row">
          {{row.item.dispatchID}}
        </template>

        <template slot="driver" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <span v-else-if="row.item.driver">
            <span v-if="row.item.driver_overlap" class="overlap-badge" :title="overlap_driver_title(row.item)">⚠</span>
            <a
              v-if="!cardView && driverHosLink(row.item)"
              target="_blank"
              class="actuals-telematics-link"
              :class="{'overlap-resource': row.item.driver_overlap}"
              :href="driverHosLink(row.item)"
              :title="'Open Samsara driver HOS report for ' + row.item.driver"
            >{{ row.item.driver }}</a>
            <span v-else :class="{'overlap-resource': row.item.driver_overlap}">{{ row.item.driver }}</span>
          </span>
          <span v-else-if="!cardView" class="actuals-missing-driver" title="No driver assigned on dispatch">(missing)</span>
        </template>

        <template slot="actuals_chart" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <div
            v-else
            class="actuals-details-cell actuals-details-cell--stacked"
          >
            <button
              type="button"
              class="actuals-details-icon-btn"
              title="Show Actuals Details"
              :disabled="hos_modal_loading"
              @click.stop="open_hos_modal(row.item)"
            >
              <i class="fa fa-area-chart" aria-hidden="true"></i>
            </button>
            <overlap-correction-icon-btn
              v-if="has_overlap_correction(row.item)"
              @click="open_overlap_updates_modal(row.item)"
            />
            <actuals-feedback-controls
              :item="row.item"
              :tenant="user.tenant"
              :is-tbn="true"
              :audit-mode="feedback_audit_mode_enabled"
              @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)"
              @feedback-saved="on_feedback_saved"
            />
          </div>
        </template>

        <template slot="day_of_trip" slot-scope="row">
          <span v-if="row.item.final_day_of_trip">{{row.item.day_of_trip}} of {{row.item.final_day_of_trip}}</span>
          <span v-else>{{row.item.day_of_trip}}</span>
        </template>

        <template slot="last_updated" slot-scope="row">
          {{row.item.last_updated | formatDateTimeSmall}}
        </template>

        <template slot="startBase" slot-scope="row">
          {{row.item.startBase | trim(30)}}
        </template>

        <template slot="finishBase" slot-scope="row">
          {{row.item.finishBase | trim(30)}}
        </template>

        <template slot="vehicle_type" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <span v-else>{{ row.item.vehicle_type }}</span>
        </template>

        <template slot="vehicle" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <span v-else-if="!cardView || row.item.vehicle || row.item.vehicle_overlap">
            <span v-if="row.item.vehicle_overlap" class="overlap-badge" :title="overlap_vehicle_title(row.item)">⚠</span>
            <a
              v-if="!cardView && row.item.vehicle && vehicleSamsaraLink(row.item)"
              target="_blank"
              class="actuals-telematics-link"
              :class="{'overlap-resource': row.item.vehicle_overlap}"
              :href="vehicleSamsaraLink(row.item)"
              :title="'Open Samsara vehicle trips for ' + row.item.vehicle"
            >{{ row.item.vehicle }}</a>
            <span v-else :class="{'overlap-resource': row.item.vehicle_overlap}">{{ row.item.vehicle }}</span>
          </span>
        </template>

        <template slot="booking_updated" slot-scope="row">
          {{row.item.booking_updated | formatDateTimeSmall}}
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
        </template>

        <template slot="last_updated" slot-scope="row">
          {{row.item.last_updated | formatDateTimeSmall}}
        </template>

        <!-- card-only virtual columns. they exist only in cardView, so the desktop
             table is untouched: b-table renders a slot for any field key it is
             given, and card_field_keys only adds these below the card breakpoint. -->
        <!-- card-only virtual columns. they exist only in cardView, so the desktop
             table is untouched: b-table renders a slot for any field key it is
             given, and card_field_keys only adds these below the card breakpoint.
             each is written on one line so an unpopulated cell is genuinely
             :empty and its grid row collapses instead of leaving a blank line. -->
        <template slot="card_ids" slot-scope="row"><span v-if="row.item._overlap_group_header" class="overlap-section-heading" :class="'overlap-section-heading--' + row.item._overlap_group_type">{{ row.item._overlap_group_line }}</span><span v-else-if="row.item.bookingID">{{ row.item.bookingID }}<span v-if="card_trip_day(row.item)" class="actuals-card-ids-sub"> / {{ card_trip_day(row.item) }}</span></span></template>

        <template slot="card_time" slot-scope="row"><span v-if="card_actual_start(row.item)" class="actuals-card-time--actual" title="Actual start">{{ card_actual_start(row.item) | formatDateTimeSmall }}</span><span v-else-if="!row.item._overlap_group_header && row.item.start_datetime" class="actuals-card-time--planned" title="Planned start - no actual recorded yet">{{ row.item.start_datetime | formatDateTimeSmall }}</span></template>

        <template slot="card_finish" slot-scope="row"><span v-if="card_actual_finish(row.item)" class="actuals-card-time--actual" title="Actual finish">{{ card_actual_finish(row.item) | formatDateTimeSmall }}</span><span v-else-if="!row.item._overlap_group_header && row.item.finish_datetime" class="actuals-card-time--planned" title="Planned finish - no actual recorded yet">{{ row.item.finish_datetime | formatDateTimeSmall }}</span></template>

        <!-- the flag (and, in audit mode, the thumbs) bottom-right, only on rows
             the processor reached a verdict on -->
        <template slot="card_feedback" slot-scope="row"><actuals-feedback-controls v-if="card_has_actuals(row.item)" :item="row.item" :tenant="user.tenant" :is-tbn="true" :audit-mode="feedback_audit_mode_enabled" @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)" @feedback-saved="on_feedback_saved"/></template>

        <template slot="card_base" slot-scope="row"><span v-if="card_base_text(row.item)">{{ card_base_text(row.item) }}</span></template>

        <template slot="card_route" slot-scope="row"><span v-if="card_route_text(row.item)">{{ card_route_text(row.item) }}</span></template>

        <template slot="card_totals" slot-scope="row"><span v-if="card_totals_text(row.item)" class="actuals-card-totals" title="Recorded duration and distance">{{ card_totals_text(row.item) }}</span></template>

        <template slot="start_datetime" slot-scope="row">
          {{row.item.start_datetime | formatDateTimeSmall}}
        </template>
        
        <template slot="finish_datetime" slot-scope="row">
          {{row.item.finish_datetime | formatDateTimeSmall}}
        </template>

        <template slot="driver_start_date" slot-scope="row">
          <span v-if="row.item.day_of_trip == 1">
            {{row.item.driver_start_date | formatDateTimeSmall}}
          </span>
          <span v-else></span>
        </template>

        <template slot="driver_finish_date" slot-scope="row">
          <span v-if="row.item.day_of_trip == row.item.final_day_of_trip">
            {{row.item.driver_finish_date | formatDateTimeSmall}}
          </span>
          <span v-else></span>
        </template>

        <template slot="routeDescription" slot-scope="row">
          {{row.item.routeDescription | trim(30)}}
        </template>

        <template slot="details" slot-scope="row">
          <div
            v-if="row.item._overlap_group_header"
            class="overlap-section-heading"
            :class="'overlap-section-heading--' + row.item._overlap_group_type"
          >
            {{ row.item._overlap_group_line }}
          </div>
          <div v-else style="align-content: center">
            <b-button size="sm" @click="toggle_row_details(row)">
              {{ row.detailsShowing ? '-' : '+' }}
            </b-button>&nbsp;
          </div>
        </template>

        <template slot="row-details" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <div v-else style="text-align: left; padding: 10px; margin:10px; border: 1px dotted grey">

            <h5>Vehicle Segments: {{row.item.vehicleAssignment.RouteDescription}}</h5>
            <table width="100%" cellpadding="5" cellspacing="5" >
              <tr>
                <th>Departure</th>
                <th>Planned Departure</th>
                <th>Actual Departure</th>
                <th>Diff</th>
                <th>Arrival</th>
                <th>Planned Arrival</th>
                <th>Actual Arrival</th>
                <th>Diff</th>
              </tr>
              <tr v-for="segment in row.item.vehicleAssignment.VehicleSegments">
                  <td style="text-align: left !important;"><a target='_blank' :href="'https://www.google.com/maps/search/?api=1&query=' + segment.departureLatitude + ',' + segment.departureLongitude">{{segment.departureAddress | trim(50)}}</a></td>
                  <td>{{segment.departureTime | formatDateTime}}</td>
                  <td>{{segment.actualDepartureTime | formatDateTime}}</td>
                  <td>{{get_time_diff(segment.departureTime,segment.actualDepartureTime)}}</td>
                  <td style="text-align: left !important"><a target='_blank' :href="'https://www.google.com/maps/search/?api=1&query=' + segment.arrivalLatitude + ',' + segment.arrivalLongitude">{{segment.arrivalAddress | trim(50)}}</a></td>
                  <td>{{segment.arrivalTime | formatDateTime}}</td>
                  <td>{{segment.actualArrivalTime | formatDateTime}}</td>
                  <td>{{get_time_diff(segment.arrivalTime,segment.actualArrivalTime)}}</td>
              </tr>
            </table>

            <br/>
            <div v-html="row.item.actual.samsara_links"/>
            <div style="text-align: left; margin: 10px 0">
              <b-button size="sm" variant="info" title="Show Actuals Details"
                        @click="open_hos_modal(row.item)" :disabled="hos_modal_loading">
                <i class="fa fa-area-chart" aria-hidden="true"></i> Actuals Details
              </b-button>
            </div>
            <span>
              <b>Map Links: </b>
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/vehicle-track/' + user.tenant + '/' + row.item.vehicle }">Track Vehicle</router-link>
              &nbsp;|&nbsp;
              <router-link style="font-weight: bold;" :to="{ path: '/vehicle_trip_history?vehicleId=' + row.item.vehicle + '&trip_date=' + row.item.start_datetime.split('T')[0] }">Trip History</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/booking/' + row.item.bookingID + '/tbn'}">Map By Booking ID</router-link>
              <span v-if="row.item.vehicleAssignment.RouteDescription">
                &nbsp;|&nbsp;
                <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/routeSearch/' + row.item.vehicleAssignment.RouteDescription + '/tbn'}">Map By Route Desc</router-link>
              </span>
              <span v-if="row.item.organization">
                &nbsp;|&nbsp;
                <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/orgName/' + row.item.organization + '/tbn'}">Map By Org</router-link>
              </span>
              <span v-if="row.item.type == 'scheduledservice'">
                &nbsp;|&nbsp;
                <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/bookingType/' + row.item.bookingID.split('-')[0] + '/tbn'}">Line Run Map</router-link>
                &nbsp;|&nbsp;
                <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/contractName/' + row.item.bookingID.split('-')[0] + '/tbn'}">Contract Map</router-link>
              </span>
            </span>

            <div class="row" style="margin: 0px">
              <div class="col" v-if="row.item.actual">
                <b>Start Event Times:</b><br/>
                Planned Driver Start: {{row.item.driver_start_date | formatDateTimeSmall}}<br/>
                HOS On Duty: <span :class="{'hos-diff-time': hos_start_diff_highlight(row.item)}">{{recorded_hos_time(row.item.actual, 'actualClockInTime', 'actualClockInTimeOriginal', 'actualClockInAdjustment') | formatDateTime}}</span><br/>
                <span v-if="adjusted_driver_time(row.item.actual, 'actualClockInTime')">
                  Adj Driver Start: {{row.item.actual.actualClockInTime | formatDateTime}}<br/>
                </span>
                DVIR Pre Trip Time: {{row.item.actual.nearestPreTripDVIRDateTime | formatDateTime}}<br/>
                Vehicle Key On: {{row.item.actual.actualIgnitionOnTime | formatDateTime}}<br/>
                Vehicle Leave Time: {{row.item.actual.actualStartLocationTime | formatDateTime}}<br/>
                First Pickup Time: {{row.item.actual.actualFirstPickupTime | formatDateTime}}<br/>
                Trip Start Time: {{row.item.actual.actualStartTripTime | formatDateTime}}<br/>
                TBN CheckIn Time: {{row.item.actual.tbnCheckInTime | formatDateTime}}
              </div>

              <div class="col" v-if="row.item.actual">
                <b>Finish Event Times:</b><br/>
                Planned Driver Finish: {{row.item.driver_finish_date | formatDateTimeSmall}}<br/>
                HOS Off Duty: <span :class="{'hos-diff-time': hos_finish_diff_highlight(row.item)}">{{recorded_hos_time(row.item.actual, 'actualClockOutTime', 'actualClockOutTimeOriginal', 'actualClockOutAdjustment') | formatDateTime}}</span><br/>
                <span v-if="adjusted_driver_time(row.item.actual, 'actualClockOutTime')">
                  Adj Driver Finish: {{row.item.actual.actualClockOutTime | formatDateTime}}<br/>
                </span>
                DVIR Post Trip Time: {{row.item.actual.nearestPostTripDVIRDateTime | formatDateTime}}<br/>
                Vehicle Key Off: {{row.item.actual.actualIgnitionOffTime | formatDateTime}}<br/>
                Vehicle Back Time: {{row.item.actual.actualFinishLocationTime | formatDateTime}}<br/>
                Last DropOff Time: {{row.item.actual.actualLastDropOffTime | formatDateTime}}<br/>
                Trip Finish Time: {{row.item.actual.actualFinishTripTime | formatDateTime}}

              </div>

              <div class="col" v-if="row.item.actual">
                <b>Trip Duration (minutes)</b><br/>
                Total Duration: {{row.item.actual.actualTotalDuration}} {{minutesToHours(row.item.actual.actualTotalDuration)}}<br/>
                Driving Duration: {{row.item.actual.actualDrivingDuration}} {{minutesToHours(row.item.actual.actualDrivingDuration)}}<br/>
                Wait Duration: {{row.item.actual.actualWaitDuration}} {{minutesToHours(row.item.actual.actualWaitDuration)}}<br/>
                Live Duration: {{row.item.actual.actualLiveDuration}} {{minutesToHours(row.item.actual.actualLiveDuration)}}<br/>
                Dead Duration: {{row.item.actual.actualDeadDuration}} {{minutesToHours(row.item.actual.actualDeadDuration)}}<br/>
              </div>

              <div class="col" v-if="row.item.actual">
                <b>Distances (miles)</b><br/>
                Total Trip Distance: {{row.item.actual.actualTotalDistance}}<br/>
                Start Odometer: {{row.item.actual.actualStartOdometer}}<br/>
                First Pickup Odometer: {{row.item.actual.actualFirstPickupOdometer}}<br/>
                Last Dropoff Odometer: {{row.item.actual.actualLastDropOffOdometer}}<br/>
                End Odometer: {{row.item.actual.actualEndOdometer}}<br/>
                <span v-if="row.item.actual.actualEndOdometer && row.item.actual.actualStartOdometer">
                  Total Odometer Distance: {{row.item.actual.actualEndOdometer - row.item.actual.actualStartOdometer}}
                </span>
              </div>

              <div class="col" v-if="row.item.actual">
                <b>State Distances (miles)</b><br/>
                <div v-for="dist in row.item.actual.actualDistances">
                  {{dist.jurisdiction}} : {{dist.distance}}
                </div>
              </div>
            </div>

            <div v-if="row.item.vehicleAssignment" style="text-align: left; margin: 10px">
              <button @click="toggle_div(row.item.actual,'isAssignmentVisible')">show assignment</button>
              <br/>
              <span :style="row.item.actual.isAssignmentVisible ? '' : 'display:none'">
                <pre style="text-align: left">{{row.item.vehicleAssignment | prettyJsonObj}}</pre>
              </span>
            </div>

            <div v-if="row.item.actual.json" style="text-align: left; margin: 10px">
              <button @click="toggle_div(row.item.actual,'isActualJsonVisible')">show actual results</button>
              <br/>
              <span :style="row.item.actual.isActualJsonVisible ? '' : 'display:none'">
                <pre style="text-align: left">{{row.item.actual.json | prettyJsonObj}}</pre>
              </span>
            </div>

            <div v-if="row.item.booking_history" style="text-align: left; margin: 10px">
              <button @click="toggle_div(row.item.actual,'isHistoryVisible')">show booking update history</button>
              <br/>
              <span :style="row.item.actual.isHistoryVisible ? '' : 'display:none'">

                  <div class="row" style="margin:5px" v-for="booking_processed_time in row.item.booking_history.split(',')">
                    <div style="max-width: 250px" class="col">{{booking_processed_time | formatDateTime}}</div>
                    <div class="col">
                      <a href="#" v-on:click.prevent="show_booking_history(booking_processed_time)">show/hide booking</a>
                      <span v-if="booking_processed_time in booking_history_json">
                        <br/><hr/>
                        <b>Differences</b><br/><hr/>
                        {{booking_history_json[booking_processed_time].payload_diff}}
                      </span>
                      <span v-if="booking_processed_time in booking_history_json">
                        <br/><hr/>
                        <b>Full Booking</b><br/><hr/>
                        <pre>{{booking_history_json[booking_processed_time].payload}}</pre>
                      </span>
                    </div>
                  </div>
              </span>
            </div>

          </div>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>

    <actuals-hos-timeline-modal
      :visible.sync="hos_modal_visible"
      :loading="hos_modal_loading"
      :error="hos_modal_error"
      :summary="hos_modal_summary"
      :events="hos_modal_events"
      :ignition-events="hos_modal_ignition_events"
      :feedback-item="hos_modal_item"
      :tenant="user.tenant"
      :feedback-enabled="feedback_audit_mode_enabled"
      :is-tbn="true"
      :show-admin-actions="isEffectiveAdmin"
      :reprocessing="hos_modal_reprocessing"
      :sending-to-tbn="hos_modal_sending_to_tbn"
      :nav-prev-enabled="hos_modal_nav_has_prev"
      :nav-next-enabled="hos_modal_nav_has_next"
      :nav-label="hos_modal_nav_label"
      @prev="hos_modal_go_prev"
      @next="hos_modal_go_next"
      @feedback-saved="on_feedback_saved"
      @reprocess="reprocess_hos_modal_actual"
      @send-to-tbn="send_hos_modal_actual_to_tbn"
      @reload="reload_hos_modal"
      @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)"
      @hidden="reset_hos_modal"
    />

    <actuals-error-summary-modal
      :visible.sync="error_summary_modal_visible"
      :loading="error_summary_modal_loading"
      :error="error_summary_modal_error"
      :summary="error_summary_modal_summary"
      :error-categories="error_summary_modal_categories"
      :error-groups="error_summary_modal_groups"
      :troubleshooter-link="error_summary_troubleshooter_link"
      @hidden="reset_error_summary_modal"
    />

    <actuals-overlap-updates-modal
      :visible.sync="overlap_updates_modal_visible"
      :booking-label="overlap_updates_modal_booking_label"
      :updates="overlap_updates_modal_updates"
      @hidden="reset_overlap_updates_modal"
    />
  </div>
</template>

<script>

import {api_url, get_booking_bases, get_tbn_booking_history, get_tbn_actuals, get_tbn_actuals_error_summary, get_tbn_actuals_hos_timeline, reprocess_actuals, reprocess_tbn_actual, send_tbn_actual_outbound, recheck_tbn_overlaps, clear_tbn_actuals_feedback_for_date_range} from '../networking/tbn_apis'
    import {mapState} from "vuex";
    import moment from 'moment'
import {justDate} from "@/helpers/dates";
import {
  clampDateRangeForLargeFleet,
  configureTenantBaseOptions,
  isDateRangeExceedingLargeFleetLimit,
} from '@/helpers/actuals_base_requirement'
import { consecutiveEntriesOverlapMoreThanOneMinute } from '@/helpers/actuals_overlap'
import { excludeSkippedUnlessSelected, filterActualsItemsByStatus } from '@/helpers/actuals_status_filter'
import { countActualsFeedback, matchesActualsFeedbackFilter, findActualsFeedbackRow, ACTUALS_FEEDBACK_FILTER_TOOLTIPS } from '@/helpers/actuals_feedback_filter'
import { isFeedbackAuditModeEnabled } from '@/helpers/feedback_audit_mode'
import { countActualsTripDuration, matchesActualsTripDurationFilter, ACTUALS_TRIP_DURATION_FILTER_TOOLTIPS } from '@/helpers/actuals_trip_duration_filter'
import { actualsBookingTypeFilters, actualsBookingTypeTooltips, countActualsBookingType, matchesActualsBookingTypeFilter } from '@/helpers/actuals_booking_type_filter'
import {
  buildDistinctColumnFilterOptions,
  driverColumnValue,
  matchesActualsColumnFilter,
  vehicleColumnValue,
} from '@/helpers/actuals_column_filter'
import error_category_popover from '@/components/error_category_popover'
import { categorizeError } from '@/helpers/actuals_error_categories'
import actuals_troubleshooter_help from '@/components/actuals_troubleshooter_help'
import actuals_hos_timeline_modal from '@/components/actuals_hos_timeline_modal'
import actuals_error_summary_modal from '@/components/actuals_error_summary_modal'
import actuals_overlap_updates_modal from '@/components/actuals_overlap_updates_modal'
import overlap_correction_icon_btn from '@/components/overlap_correction_icon_btn'
import actuals_feedback_controls from '@/components/actuals_feedback_controls'
import { hasOverlapCorrection } from '@/helpers/overlap_updates_format'
import { hosModalNavState, hosModalPageForIndex } from '@/helpers/actuals_hos_modal_nav'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'
import vsmMobileView from '@/mixins/vsm_mobile_view'
import axios from 'axios';



    export default {
      name: 'actuals_tbn',
      components: {
        'error-category-popover': error_category_popover,
        'actuals-troubleshooter-help': actuals_troubleshooter_help,
        'actuals-hos-timeline-modal': actuals_hos_timeline_modal,
        'actuals-error-summary-modal': actuals_error_summary_modal,
        'actuals-overlap-updates-modal': actuals_overlap_updates_modal,
        'overlap-correction-icon-btn': overlap_correction_icon_btn,
        'actuals-feedback-controls': actuals_feedback_controls,
      },

      mixins: [vsmMobileView],

      data() {
        return {
          items: [],
          hos_modal_nav_items: [],
          items_full: [],
          selected_status: null,
          selected_feedback: null,
          selected_trip_duration: null,
          selected_booking_type: null,
          clearing_feedback: false,
          feedbackFilterTooltips: ACTUALS_FEEDBACK_FILTER_TOOLTIPS,
          tripDurationFilterTooltips: ACTUALS_TRIP_DURATION_FILTER_TOOLTIPS,
          bookingTypeFilterTooltips: actualsBookingTypeTooltips('tbn'),
          summary: [],
          loading: true,
          base_options: [],
          base_required: false,
          bases_loaded: false,
          selected_base: '',
          details_fromDate: justDate(moment()),
          details_toDate: justDate(moment()),
          // the window the grid below is actually showing. a hand-picked date only
          // moves details_*Date, so these two lag it until Search is pressed
          loaded_fromDate: '',
          loaded_toDate: '',
          quick_range_options: [
            { value: 1, label: '1d', title: 'Today' },
            { value: 3, label: '3d', title: 'Last 3 days' },
            { value: 7, label: '7d', title: 'Last 7 days' },
          ],
          show_custom_dates: false,
          totalRows: 1,
          currentPage: 1,
          perPage: 100,
          pages: [],
          actuals_filter: 'finish',
          booking_filter: '',
          selected_driver: '',
          selected_vehicle: '',
          booking_search: '',
          booking_history_json: {},
          hos_modal_visible: false,
          hos_modal_loading: false,
          hos_modal_error: '',
          hos_modal_summary: {},
          hos_modal_events: [],
          hos_modal_ignition_events: [],
          hos_modal_item: null,
          hos_modal_reprocessing: false,
          hos_modal_sending_to_tbn: false,
          recheck_overlaps_running: false,
          // Trip / Type / Overlaps and the driver+vehicle selects all sit behind
          // the More chip next to the Filter box
          results_filters_open: false,
          error_summary_modal_visible: false,
          error_summary_modal_loading: false,
          error_summary_modal_error: '',
          error_summary_modal_summary: {},
          error_summary_modal_categories: [],
          error_summary_modal_groups: [],
          overlap_updates_modal_visible: false,
          overlap_updates_modal_item: null,
          copied_booking_id: null,
          copied_booking_id_timer: null,
          overlap_colspan_observer: null,
          actuals_filter_options: [
            { value: 'finish', text: 'By Finish Date' },
            { value: 'start', text: 'By Start Date' },
            { value: 'overlap', text: 'Overlaps Range' },
          ],
          hos_diff_threshold_minutes: null,
          hos_diff_threshold_options: [
            { value: null, text: '\u2014' },
            { value: 15, text: '15m' },
            { value: 30, text: '30m' },
            { value: 60, text: '60m' },
            { value: 120, text: '120m' },
          ],
          fields: [
            { key: 'details', label: '', sortable: false},
            { key: 'ID', label: 'ID', sortable: true, thStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' }},
            { key: 'bookingID', sortable: true},
            //{ key: 'bookingStatus', label: 'Booking Status', sortable: true},
            //{ key: 'vehicleAssignmentStatus', label: 'Assign Status', sortable: true},
            { key: 'vehicleAssignmentID', label:'AssignID', sortable: true},
            { key: 'dispatchID', label:'DispatchID', sortable: true},
            { key: 'routeDescription', label:'Route', sortable: true, thStyle: { width: "200px" }},
            { key: 'vehicle_type', label:'VehType', sortable: true, thStyle: { width: '100px', minWidth: '100px' }},
            { key: 'vehicle', label:'Vehicle', sortable: true, thStyle: { width: '120px', minWidth: '120px' }},
            { key: 'driver', sortable: true, label: 'Driver', thStyle: { width: "140px" }},
            { key: 'startBase', sortable: true, label: 'Start Base', thStyle: { width: "140px" }},
            { key: 'finishBase', sortable: true, label: 'Finish Base', thStyle: { width: "140px" }},
            { key: 'day_of_trip', label: 'Trip Day', sortable: true},
            { key: 'driver_start_date', label:'Driver Start', sortable: true},
            { key: 'start_datetime', label:'First Departure', sortable: true},
            { key: 'finish_datetime', label: 'Last Arrival', sortable: true},
            { key: 'driver_finish_date', label:'Driver Finish', sortable: true},
            { key: 'booking_updated', label:'Booking Update', sortable: true, thStyle: { width: '120px' }},
            { key: 'processed_time', label:'Actuals Processed', sortable: true, thStyle: { width: '120px' }},
            { key: 'last_updated', label:'Actuals Update', sortable: true, thStyle: { width: '120px' }},
            { key: 'status_msg', label: 'Status Message', sortable: true, thStyle: { width: "420px", minWidth: '315px' }, thClass: 'actuals-status-msg-col', tdClass: 'actuals-status-msg-col' },
            { key: 'actuals_chart', label: '', sortable: false, thStyle: { width: "44px" }},
          ]
          ,
          options: {
            format: 'MM-DD-YYYY',
            useCurrent: true,
            minDate: moment().subtract(400, 'days'),
            maxDate: moment().add(180, 'days'),
            keepOpen: false,
            showClear: true,
            showClose: true,
            toolbarPlacement: 'top',
            widgetPositioning: {
              horizontal: 'left',
              vertical: 'bottom',
            },
          },

        }
      },

      async created() {
        this.fields_all = this.fields
        await this.load_bases()
        this.check_features()
      },
      methods: {
        async check_features() {
          this.fields = this.fields_all
          if (!this.isEffectiveAdmin) {
            this.fields = this.fields.filter(item => (item.key != 'ID' && item.key != 'vehicleAssignmentID' && item.key != 'dispatchID'))
          }
          this.load_data()
        },
        resetTenantBases() {
          this.selected_base = ''
          this.base_required = false
          this.base_options = []
          this.bases_loaded = false
        },
        applyBaseOptions(baseOptions) {
          const { base_required, base_options } = configureTenantBaseOptions(baseOptions)
          this.base_required = base_required
          this.base_options = base_options
          this.bases_loaded = true
          if (this.base_required)
            this.applyLargeFleetDateRange('from')
        },
        async load_bases() {
          if (!this.user || !this.user.tenant)
            return
          try {
            const { value: { data = {} } } = await get_booking_bases(this.user.tenant)
            this.applyBaseOptions((data.summary && data.summary.base_options) || [])
          } catch (e) {
            this.$emit('showAlert', 'error retrieving bases, try logging in again or contact support if needed', 'danger', 5000)
          }
        },
        applyLargeFleetDateRange(changedField) {
          if (!this.base_required)
            return
          const { fromDate, toDate } = clampDateRangeForLargeFleet(
            this.details_fromDate,
            this.details_toDate,
            changedField
          )
          this.details_fromDate = fromDate
          this.details_toDate = toDate
        },
        async search_by_date_range() {
          this.booking_search = ''
          if (this.$route.query.booking_id) {
            const query = { ...this.$route.query }
            delete query.booking_id
            await this.$router.replace({ query })
          }
          this.load_data()
        },
        async search_by_id() {
          this.load_data()
        },
        // the toolbar Search button drives both lookups: a typed booking id when
        // there is one, otherwise the date window sitting in the pickers
        run_search() {
          if (this.booking_search)
            this.search_by_id()
          else
            this.search_by_date_range()
        },
        apply_date_range_from_results(results) {
          if (!results || !results.length)
            return
          let earliest = null
          let latest = null
          for (const item of results) {
            for (const value of [item.start_datetime, item.finish_datetime]) {
              if (!value)
                continue
              const m = moment(value)
              if (!m.isValid())
                continue
              if (!earliest || m.isBefore(earliest))
                earliest = m
              if (!latest || m.isAfter(latest))
                latest = m
            }
          }
          if (earliest && latest) {
            this.details_fromDate = earliest.format('MM-DD-YYYY')
            this.details_toDate = latest.format('MM-DD-YYYY')
          }
        },
        async load_data() {
            if (isDateRangeExceedingLargeFleetLimit(this.details_fromDate, this.details_toDate, this.base_required)) {
              this.applyLargeFleetDateRange('to')
              this.$emit('showAlert', 'please limit date range to 7 days or less', 'warning', 5000)
              return
            }
            this.loading = true
            try {
              if (this.$route.query.booking_id && !this.booking_search)
                this.booking_search = this.$route.query.booking_id

              const {value: {data = []}} = await get_tbn_actuals(this.user.tenant, this.actuals_filter, this.booking_search, this.selected_base, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
              this.summary = data.summary
              moment.tz.setDefault(this.summary.timezone)
              this.items = data.results
              this.items_full = data.results
              this.selected_driver = ''
              this.selected_vehicle = ''
              this.detect_overlaps(this.items_full)
              if (!this.bases_loaded && data.summary && data.summary.bases)
                this.applyBaseOptions(data.summary.bases)
              this.filter_results()

              if (this.booking_search)
                this.apply_date_range_from_results(this.items_full)

              // the grid now matches the pickers again, which clears the Search
              // button's pending state and re-points the CSV export
              this.loaded_fromDate = this.details_fromDate
              this.loaded_toDate = this.details_toDate
            } catch(e) {
              this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
            }
            this.loading = false
        },
        async reprocess_actuals() {
          try {
            if (this.items_full.length > 200) {
              alert("please limit actuals reprocessing to 200 bookings or less or contact support if needed")
              return
            }

            if (confirm("are you sure you want to reprocess actuals for this date range? (note - this is only necessary if HOS edits were made)")) {
              this.$emit('showAlert', 'sending request to reprocess actuals', 'info', 5000)
              const {value: {data = []}} = await reprocess_actuals(this.user.tenant, this.actuals_filter, this.selected_base, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
              this.$emit('showAlert', 'reprocess request sent', 'success', 5000)
            }
          } catch(e) {
            this.$emit('showAlert', 'error reprocessing actuals, try a smaller date range or contact support if needed', 'danger', 5000)
          }
        },
        async recheck_overlaps() {
          if (this.recheck_overlaps_running)
            return
          const rangeLabel = `${this.details_fromDate} to ${this.details_toDate}`
          if (!confirm(`Re-split overlapping driver/vehicle time and mileage for ${rangeLabel}? This may update actuals on affected bookings.`))
            return
          try {
            this.recheck_overlaps_running = true
            this.$emit('showAlert', 'rechecking overlapping actuals for this date range', 'info', 5000)
            const {value: {data = {}}} = await recheck_tbn_overlaps(
              this.user.tenant,
              moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
              moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
            )
            const corrected = (data && data.corrected) ? data.corrected.length : 0
            this.$emit('showAlert', 'overlap recheck complete' + (corrected ? (', split ' + corrected + ' booking(s)') : ', no overlaps found'), 'success', 5000)
            await this.load_data()
          } catch(e) {
            this.$emit('showAlert', 'error rechecking overlaps, try a smaller date range or contact support if needed', 'danger', 5000)
          } finally {
            this.recheck_overlaps_running = false
          }
        },
        async open_hos_modal(item) {
          if (!item || !item.bookingID)
            return
          this.hos_modal_item = item
          this.hos_modal_visible = true
          await this.load_hos_modal()
        },
        async load_hos_modal() {
          const item = this.hos_modal_item
          if (!item || !item.bookingID)
            return
          this.hos_modal_loading = true
          this.hos_modal_error = ''
          this.hos_modal_summary = {}
          this.hos_modal_events = []
          this.hos_modal_ignition_events = []
          try {
            const { value = {} } = await get_tbn_actuals_hos_timeline(
              this.user.tenant,
              item.bookingID,
              item.driver,
              item.vehicleAssignmentID,
              item.dispatchID,
              item.day_of_trip,
              item.ID,
            )
            const payload = (value.data && (value.data.data || value.data)) || {}
            this.hos_modal_error = payload.error || ''
            this.hos_modal_summary = payload.summary || {}
            this.hos_modal_events = payload.events || []
            this.hos_modal_ignition_events = payload.ignition_events || []
          } catch (e) {
            const resp = e.response && e.response.data
            const payload = resp && (resp.data || resp)
            this.hos_modal_error = (payload && payload.error) ||
              (resp && resp.message) ||
              (resp && resp.error) ||
              'error loading actuals details, try logging in again or contact support if needed'
          }
          this.hos_modal_loading = false
        },
        async reload_hos_modal() {
          await this.load_hos_modal()
        },
        async reprocess_hos_modal_actual() {
          const item = this.hos_modal_item
          if (!item || !item.ID || this.hos_modal_reprocessing)
            return
          this.hos_modal_reprocessing = true
          try {
            await reprocess_tbn_actual(this.user.tenant, item.ID)
            // reprocessing is synchronous on the backend; reload the details and
            // refresh the underlying table so the row reflects new results
            await this.load_hos_modal()
            this.filter_results()
            this.$emit('showAlert', 'actual reprocessed', 'success', 4000)
          } catch (e) {
            const resp = e.response && e.response.data
            const payload = resp && (resp.data || resp)
            const msg = (payload && payload.error) ||
              (resp && resp.message) ||
              'error reprocessing actual, try again or contact support if needed'
            this.$emit('showAlert', msg, 'danger', 6000)
          }
          this.hos_modal_reprocessing = false
        },
        async send_hos_modal_actual_to_tbn() {
          const item = this.hos_modal_item
          if (!item || !item.ID || this.hos_modal_sending_to_tbn)
            return
          if (!confirm("send these actuals to TBN now? (this sends even if outbound actuals are disabled for this tenant)"))
            return
          this.hos_modal_sending_to_tbn = true
          try {
            const {value: {data = {}}} = await send_tbn_actual_outbound(this.user.tenant, item.ID)
            if (data && data.sent)
              this.$emit('showAlert', 'actuals sent to TBN', 'success', 4000)
            else
              this.$emit('showAlert', 'actuals not sent to TBN' + ((data && data.message) ? (': ' + data.message) : ''), 'warning', 8000)
          } catch (e) {
            const resp = e.response && e.response.data
            const payload = resp && (resp.data || resp)
            const msg = (payload && payload.error) ||
              (resp && resp.message) ||
              'error sending actuals to TBN, try again or contact support if needed'
            this.$emit('showAlert', msg, 'danger', 6000)
          }
          this.hos_modal_sending_to_tbn = false
        },
        async hos_modal_navigate(delta) {
          const { navItems, index } = this.hos_modal_nav
          const nextIdx = index + delta
          if (nextIdx < 0 || nextIdx >= navItems.length)
            return
          const page = hosModalPageForIndex(nextIdx, this.perPage)
          if (this.currentPage !== page)
            this.currentPage = page
          await this.open_hos_modal(navItems[nextIdx])
        },
        hos_modal_go_prev() {
          return this.hos_modal_navigate(-1)
        },
        hos_modal_go_next() {
          return this.hos_modal_navigate(1)
        },
        reset_hos_modal() {
          this.hos_modal_loading = false
          this.hos_modal_reprocessing = false
          this.hos_modal_sending_to_tbn = false
          this.hos_modal_error = ''
          this.hos_modal_summary = {}
          this.hos_modal_events = []
          this.hos_modal_ignition_events = []
          this.hos_modal_item = null
        },
        async open_error_summary_modal() {
          this.error_summary_modal_visible = true
          this.error_summary_modal_loading = true
          this.error_summary_modal_error = ''
          this.error_summary_modal_summary = {}
          this.error_summary_modal_categories = []
          this.error_summary_modal_groups = []
          try {
            const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
            const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
            const { value: { data = {} }} = await get_tbn_actuals_error_summary(
              this.user.tenant,
              from,
              to,
              this.actuals_filter,
              this.selected_base,
            )
            this.error_summary_modal_summary = data.summary || {}
            this.error_summary_modal_categories = data.error_categories || []
            this.error_summary_modal_groups = data.error_groups || []
          } catch (e) {
            this.error_summary_modal_error = 'error loading error summary, try logging in again or contact support if needed'
          }
          this.error_summary_modal_loading = false
        },
        reset_error_summary_modal() {
          this.error_summary_modal_loading = false
          this.error_summary_modal_error = ''
          this.error_summary_modal_summary = {}
          this.error_summary_modal_categories = []
          this.error_summary_modal_groups = []
        },
        async copy_booking_id(bookingID) {
          if (!bookingID)
            return
          try {
            await copyToClipboard(String(bookingID))
            this.copied_booking_id = bookingID
            if (this.copied_booking_id_timer)
              clearTimeout(this.copied_booking_id_timer)
            this.copied_booking_id_timer = setTimeout(() => {
              this.copied_booking_id = null
            }, 2000)
          } catch (e) {
            this.$emit('showAlert', 'unable to copy ID', 'danger', 3000)
          }
        },
        booking_search_route(bookingId) {
          return {
            name: 'actuals_tbn',
            query: { booking_id: bookingId },
          }
        },
        driverHosLink(item) {
          if (!item || !this.isSamsaraTenant)
            return null
          if (item.driver_hos_link)
            return item.driver_hos_link
          if (!this.summary || !this.summary.org_id || !item.samsara_driver_id)
            return null
          const endMs = this.driverHosEndMs(item)
          if (!endMs)
            return null
          return `https://cloud.samsara.com/o/${this.summary.org_id}/fleet/reports/hos/driver/driver/${item.samsara_driver_id}?end_ms=${endMs}`
        },
        vehicleSamsaraLink(item) {
          if (!item || !this.isSamsaraTenant || !item.vehicle)
            return null
          return item.samsara_vehicle_link || null
        },
        driverHosEndMs(item) {
          const finish = item && (item.driver_finish_date || item.finish_datetime)
          if (!finish)
            return null
          const finishDate = finish instanceof Date ? finish : new Date(finish)
          if (Number.isNaN(finishDate.getTime()))
            return null
          const dayStart = new Date(finishDate)
          dayStart.setHours(0, 0, 0, 0)
          return Math.floor(dayStart.getTime()) + 43199999 + 25200000
        },
        async show_booking_history(processed_time) {
          if (this.booking_history_json[processed_time])
            delete this.booking_history_json[processed_time]
          else {
            const {value: {data = []}} = await get_tbn_booking_history(this.user.tenant, processed_time)
            this.booking_history_json[processed_time] = data
          }
          this.$forceUpdate()
        },
        async download() {
          this.$emit('showAlert', 'processing download request', 'info', 5000)
          // export the window the grid is showing, not one picked but not yet searched
          const fromDate = moment(this.loaded_fromDate || this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const toDate = moment(this.loaded_toDate || this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const tenantName = this.user.tenant && this.user.tenant.indexOf('-') !== -1
            ? this.user.tenant.split('-')[0]
            : (this.user.tenant || 'actuals')
          const statusSuffix = this.selected_status ? '-' + this.selected_status : ''
          const filename = `actuals-${tenantName}-${fromDate}-to-${toDate}${statusSuffix}.csv`
          axios.defaults.headers.common.Accept = {}
          try {
            const response = await axios.post(
              `${api_url}tbn/actuals/download`,
              {
                tenant: this.user.tenant,
                fromDate,
                toDate,
                filter: this.actuals_filter,
                base: this.selected_base || '',
                booking_search: this.booking_search || '',
                status: this.selected_status || null,
                booking_filter: this.booking_filter || '',
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                  Accept: 'text/csv',
                },
                responseType: 'blob',
              }
            )
            axios.defaults.headers.common.Accept = 'application/json'
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            window.URL.revokeObjectURL(url)
            link.remove()
            this.$emit('showAlert', 'download complete', 'success', 5000)
          } catch (e) {
            axios.defaults.headers.common.Accept = 'application/json'
            this.$emit('showAlert', 'actuals data could not be downloaded.', 'danger')
          }
        },
        shift_detail_graph_date(shift, unit) {
          if (unit === 'months') {
            if (this.base_required) {
              this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift * 7, 'days').format('MM-DD-YYYY')
              this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift * 7, 'days').format('MM-DD-YYYY')
              this.applyLargeFleetDateRange('from')
            } else {
              const month = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'months')
              let from = month.clone().startOf('month')
              let to = month.clone().endOf('month')
              const min = moment(this.options.minDate)
              const max = moment(this.options.maxDate)
              if (from.isBefore(min, 'day'))
                from = min.clone()
              if (to.isAfter(max, 'day'))
                to = max.clone()
              if (from.isAfter(to, 'day'))
                from = to.clone()
              this.details_fromDate = from.format('MM-DD-YYYY')
              this.details_toDate = to.format('MM-DD-YYYY')
            }
          } else {
            this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
            this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
          }
          this.booking_id_filter = ''
        },
        set_quick_range(days) {
          this.details_fromDate = moment(this.details_toDate, 'MM-DD-YYYY').subtract(days - 1, 'days').format('MM-DD-YYYY')
          this.scheduleRangeLoad()
        },
        nudge_range_days(shift) {
          this.shift_detail_graph_date(shift, 'days')
          this.scheduleRangeLoad()
        },
        // the date watchers clamp the range (large-fleet limit, from-after-to) on
        // the next flush, so the reload waits for them rather than searching the
        // pre-clamp dates; coalesced so one action is one request
        scheduleRangeLoad() {
          if (this._range_load_scheduled)
            return
          this._range_load_scheduled = true
          this.$nextTick(() => {
            this._range_load_scheduled = false
            this.search_by_date_range()
          })
        },
        get_time_diff(time1, time2) {
          if (time1 != null && time2 != null)
            return Math.round((new Date(time2) - new Date(time1)) / (1000 * 60)) + 'm';
          return ''
        },
        toggle_div(item,field) {
          item[field] = !item[field];
          this.$forceUpdate();
        },
        get_row_color(item, type) {
            if (!item || type !== 'row') return
            if (item._overlap_group_header)
              return 'overlap-group-header-row'
            let rowClass = item.status || ''
            if (item.driver_overlap && item.vehicle_overlap)
              rowClass += ' overlap-both'
            else if (item.driver_overlap)
              rowClass += ' overlap-driver'
            else if (item.vehicle_overlap)
              rowClass += ' overlap-vehicle'
            return rowClass.trim()
        },
        overlap_header_field_key() {
          return (this.table_fields[0] && this.table_fields[0].key) || 'details'
        },
        get_overlap_td_class(fieldKey, item) {
          if (item && item._overlap_group_header)
            return fieldKey === this.overlap_header_field_key() ? 'overlap-group-header-cell' : 'overlap-group-header-empty'
          if (fieldKey === 'details' || fieldKey === 'actuals_chart')
            return 'actuals-details-td'
          if (fieldKey === 'ID')
            return 'actuals-id-td'
          return ''
        },
        get_actuals_table_element() {
          const tableRef = this.$refs.actualsTbnTable
          if (!tableRef)
            return null
          const root = tableRef.$el || tableRef
          if (!root)
            return null
          const tables = root.querySelectorAll ? root.querySelectorAll('table.actuals-tbn-table, table.table') : []
          for (let i = 0; i < tables.length; i++) {
            if (tables[i].querySelector('tbody tr.overlap-group-header-row'))
              return tables[i]
          }
          if (root.tagName === 'TABLE')
            return root
          return root.querySelector('table.actuals-tbn-table') || root.querySelector('table')
        },
        apply_overlap_header_colspan_row(tr, colCount) {
          const headerCell = Array.from(tr.children).find(td => td.querySelector('.overlap-section-heading'))
          if (!headerCell)
            return
          Array.from(tr.children).forEach(td => {
            if (td !== headerCell)
              td.remove()
          })
          if (tr.firstElementChild !== headerCell)
            tr.insertBefore(headerCell, tr.firstElementChild)
          headerCell.colSpan = colCount
          headerCell.classList.add('overlap-group-header-cell')
          headerCell.classList.remove('overlap-group-header-empty')
        },
        stop_overlap_colspan_observer() {
          if (this.overlap_colspan_observer) {
            this.overlap_colspan_observer.disconnect()
            this.overlap_colspan_observer = null
          }
        },
        start_overlap_colspan_observer() {
          this.stop_overlap_colspan_observer()
          if (!this.show_overlap_group_headers)
            return
          this.$nextTick(() => {
            const table = this.get_actuals_table_element()
            const tbody = table && table.querySelector('tbody')
            if (!tbody)
              return
            this.overlap_colspan_observer = new MutationObserver(() => {
              this.apply_overlap_header_colspans()
            })
            this.overlap_colspan_observer.observe(tbody, { childList: true, subtree: true })
            this.apply_overlap_header_colspans()
          })
        },
        toggle_row_details(row) {
          row.toggleDetails()
          this.schedule_overlap_header_colspans()
        },
        schedule_overlap_header_colspans() {
          if (!this.show_overlap_group_headers)
            return
          const run = () => this.apply_overlap_header_colspans()
          this.$nextTick(() => {
            this.$nextTick(() => {
              run()
              if (typeof window !== 'undefined' && window.requestAnimationFrame)
                window.requestAnimationFrame(run)
            })
          })
        },
        apply_overlap_header_colspans() {
          if (!this.show_overlap_group_headers || this._applying_overlap_colspan)
            return
          this._applying_overlap_colspan = true
          try {
            const table = this.get_actuals_table_element()
            if (!table)
              return
            const colCount = this.table_fields.length
            table.querySelectorAll('tbody tr.overlap-group-header-row').forEach(tr => {
              this.apply_overlap_header_colspan_row(tr, colCount)
            })
          } finally {
            this._applying_overlap_colspan = false
          }
        },
        detect_overlaps(items) {
          if (!items || !items.length)
            return
          for (const item of items) {
            if (item._status_msg_base === undefined)
              item._status_msg_base = item.status_msg || ''
            item.driver_overlap = false
            item.vehicle_overlap = false
            item.driver_overlap_details = []
            item.vehicle_overlap_details = []
            item.driver_overlap_highlight_start = false
            item.driver_overlap_highlight_end = false
            item.vehicle_overlap_highlight_start = false
            item.vehicle_overlap_highlight_end = false
            item.driver_overlap_group = null
            item.vehicle_overlap_group = null
            item._overlap_group_label = null
            item._overlap_group_first = false
          }
          this.mark_resource_overlaps(items, 'driver', 'actualClockInTime', 'actualClockOutTime', 'driver_overlap', 'driver_overlap_details', 'driver_overlap_group', 'driver_overlap_highlight_start', 'driver_overlap_highlight_end')
          this.mark_resource_overlaps(items, 'vehicle', 'actualStartLocationTime', 'actualFinishLocationTime', 'vehicle_overlap', 'vehicle_overlap_details', 'vehicle_overlap_group', 'vehicle_overlap_highlight_start', 'vehicle_overlap_highlight_end')
          for (const item of items)
            item.status_msg = item._status_msg_base
        },
        departure_calendar_day(item) {
          if (!item || !item.start_datetime)
            return null
          const m = moment(item.start_datetime)
          return m.isValid() ? m.format('YYYY-MM-DD') : null
        },
        get_resource_entries(items, resourceKey, startField, endField) {
          const groups = {}
          for (const item of items) {
            if (item.status !== 'success')
              continue
            const resource = item[resourceKey]
            const actual = item.actual
            if (!resource || !actual)
              continue
            const start = actual[startField]
            const end = actual[endField]
            if (!start || !end)
              continue
            const sortKey = item.start_datetime || start
            if (!groups[resource])
              groups[resource] = []
            groups[resource].push({ item, start, end, sortKey })
          }
          for (const resource of Object.keys(groups))
            groups[resource].sort((a, b) => new Date(a.sortKey) - new Date(b.sortKey))
          return groups
        },
        times_overlap_by_minute(currStart, prevEnd) {
          return consecutiveEntriesOverlapMoreThanOneMinute(prevEnd, currStart)
        },
        mark_resource_overlaps(items, resourceKey, startField, endField, overlapFlag, overlapDetailsFlag, overlapGroupField, highlightStartKey, highlightEndKey) {
          const groups = this.get_resource_entries(items, resourceKey, startField, endField)
          const parent = new Map()
          const find = (item) => {
            if (!parent.has(item))
              parent.set(item, item)
            if (parent.get(item) !== item)
              parent.set(item, find(parent.get(item)))
            return parent.get(item)
          }
          const union = (a, b) => {
            parent.set(find(a), find(b))
          }
          for (const resource of Object.keys(groups)) {
            const entries = groups[resource]
            if (entries.length < 2)
              continue
            for (let i = 1; i < entries.length; i++) {
              const prev = entries[i - 1]
              const current = entries[i]
              if (!this.times_overlap_by_minute(current.start, prev.end))
                continue
              union(prev.item, current.item)
              prev.item[overlapFlag] = true
              current.item[overlapFlag] = true
              prev.item[highlightEndKey] = true
              current.item[highlightStartKey] = true
              this.add_overlap_detail(prev.item, overlapDetailsFlag, current.item.bookingID, current.start, current.end, prev.start, prev.end)
              this.add_overlap_detail(current.item, overlapDetailsFlag, prev.item.bookingID, prev.start, prev.end, current.start, current.end)
            }
            const components = new Map()
            for (const entry of entries) {
              if (!entry.item[overlapFlag])
                continue
              const root = find(entry.item)
              if (!components.has(root))
                components.set(root, [])
              const members = components.get(root)
              if (members.indexOf(entry.item) === -1)
                members.push(entry.item)
            }
            for (const members of components.values()) {
              if (members.length < 2)
                continue
              const days = [...new Set(members.map(m => this.departure_calendar_day(m)).filter(Boolean))].sort()
              const group = {
                key: resourceKey + '|' + resource + '|' + days.join(','),
                resource,
                resourceKey,
                days,
                members,
              }
              for (const member of members)
                member[overlapGroupField] = group
            }
          }
        },
        add_overlap_detail(item, overlapDetailsFlag, otherBookingID, otherStart, otherEnd, thisStart, thisEnd) {
          if (!otherBookingID)
            return
          const details = item[overlapDetailsFlag]
          if (details.some(d => d.otherBookingID === otherBookingID && d.otherStart === otherStart && d.otherEnd === otherEnd))
            return
          details.push({ otherBookingID, otherStart, otherEnd, thisStart, thisEnd })
        },
        format_overlap_time(value) {
          if (!value)
            return '?'
          const m = moment(value)
          return m.isValid() ? m.format('MM/DD HH:mm') : String(value)
        },
        formatActualTimeForStatus(value) {
          if (!value)
            return ''
          const m = moment(value)
          return m.isValid() ? m.format('MM/DD hh:mm A') : ''
        },
        format_success_status_lines(item) {
          const actual = item && item.actual
          if (!actual)
            return []

          const lines = []
          const addRange = (key, label, start, end) => {
            if (!start && !end)
              return
            lines.push({ key, type: 'range', label, start, end })
          }

          addRange('driver', 'driver', actual.actualClockInTime, actual.actualClockOutTime)
          addRange('vehicle', 'vehicle', actual.actualStartLocationTime, actual.actualFinishLocationTime)

          const meta = []
          if (actual.actualTotalDistance != null && actual.actualTotalDistance !== '') {
            let milesText = '<b>miles:</b> ' + actual.actualTotalDistance
            if (actual.actualStartOdometer && actual.actualEndOdometer)
              milesText += '&nbsp;(odo ' + (actual.actualEndOdometer - actual.actualStartOdometer) + ')'
            else
              milesText += '&nbsp;(odo missing)'
            meta.push(milesText)
          }
          if (actual.actualTotalDuration != null && actual.actualTotalDuration !== '') {
            const hrs = (Number(actual.actualTotalDuration) / 60).toFixed(2)
            meta.push('<b>min:</b> ' + actual.actualTotalDuration + ' <b>hrs:</b> ' + hrs)
          }
          if (meta.length)
            lines.push({ key: 'meta', type: 'text', text: meta.join(' &nbsp;&nbsp;') })

          return lines
        },
        show_overlap_time_highlights(item) {
          if (!item || !item.actual)
            return false
          const actual = item.actual
          const hasDriverLine = actual.actualClockInTime || actual.actualClockOutTime
          const hasVehicleLine = actual.actualStartLocationTime || actual.actualFinishLocationTime
          if (hasDriverLine && (item.driver_overlap_highlight_start || item.driver_overlap_highlight_end))
            return true
          if (hasVehicleLine && (item.vehicle_overlap_highlight_start || item.vehicle_overlap_highlight_end))
            return true
          return false
        },
        status_msgs_without_actual_times(item) {
          const html = this.status_msg_without_actual_times(item)
          if (!html)
            return []
          return html.split(',').map(part => part.trim()).filter(part => part.length > 0)
        },
        status_msg_without_actual_times(item) {
          let html = item._status_msg_base || item.status_msg || ''
          if (!html)
            return ''
          html = html.replace(/<b>driver:[\s\S]*?<br\s*\/?>/gi, '')
          html = html.replace(/<b>vehicle:<\/b>[\s\S]*?<br\s*\/?>/gi, '')
          return html.trim()
        },
        overlap_display_row_key(item) {
          if (!item)
            return ''
          return [item.ID, item.bookingID, item.start_datetime, item.day_of_trip]
            .map(v => (v == null ? '' : String(v)))
            .join('|')
        },
        build_overlap_display_items(items, resourceKey, overlapFlag, overlapGroupField) {
          const groupsByKey = {}
          for (const item of items) {
            if (!item[overlapFlag] || !item[overlapGroupField])
              continue
            const group = item[overlapGroupField]
            if (!groupsByKey[group.key])
              groupsByKey[group.key] = { group, items: [], seen: new Set() }
            const bucket = groupsByKey[group.key]
            const rowKey = this.overlap_display_row_key(item)
            if (bucket.seen.has(rowKey))
              continue
            bucket.seen.add(rowKey)
            bucket.items.push(item)
          }
          const sorted = []
          const keys = Object.keys(groupsByKey).sort((a, b) => {
            const minA = Math.min(...groupsByKey[a].items.map(i => new Date(i.start_datetime).getTime()))
            const minB = Math.min(...groupsByKey[b].items.map(i => new Date(i.start_datetime).getTime()))
            return minA - minB
          })
          for (const key of keys) {
            const { group, items: members } = groupsByKey[key]
            if (members.length < 2)
              continue
            members.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
            const resourceName = group.resource || members[0][resourceKey] || members[0].vehicle_type || ''
            sorted.push({
              _overlap_group_header: true,
              _overlap_group_type: resourceKey === 'driver' ? 'driver' : 'vehicle',
              _overlap_group_line: resourceName,
            })
            sorted.push(...members)
          }
          return sorted
        },
        overlap_driver_title(item) {
          if (!item.driver_overlap_details || !item.driver_overlap_details.length)
            return 'Driver actual clock-in/out times overlap another booking in this date range'
          return item.driver_overlap_details.map(d =>
            'Driver overlap booking ' + d.otherBookingID + ' (' + this.format_overlap_time(d.otherStart) + ' – ' + this.format_overlap_time(d.otherEnd) + ')'
          ).join('; ')
        },
        overlap_vehicle_title(item) {
          if (!item.vehicle_overlap_details || !item.vehicle_overlap_details.length)
            return 'Vehicle actual start/finish times overlap another booking in this date range'
          return item.vehicle_overlap_details.map(d =>
            'Vehicle overlap booking ' + d.otherBookingID + ' (' + this.format_overlap_time(d.otherStart) + ' – ' + this.format_overlap_time(d.otherEnd) + ')'
          ).join('; ')
        },
        has_overlap_correction(item) {
          return hasOverlapCorrection(item)
        },
        open_overlap_updates_modal(item) {
          this.overlap_updates_modal_item = item
          this.overlap_updates_modal_visible = true
        },
        reset_overlap_updates_modal() {
          this.overlap_updates_modal_item = null
        },
        overlap_correction_booking_label(item) {
          if (!item)
            return ''
          return item.bookingID || item.ID || ''
        },
        is_overlap_section_header(item) {
          return !!(item && item._overlap_group_header)
        },
        strip_overlap_section_headers(items) {
          return (items || []).filter(item => !this.is_overlap_section_header(item))
        },
        hos_delta_minutes(planned, actual) {
          if (planned == null || actual == null)
            return null
          const plannedMs = new Date(planned).getTime()
          const actualMs = new Date(actual).getTime()
          if (Number.isNaN(plannedMs) || Number.isNaN(actualMs))
            return null
          return Math.round((actualMs - plannedMs) / 60000)
        },
        hos_start_diff_over_threshold(item) {
          if (!this.hos_diff_threshold_minutes || !item || item.status !== 'success' || !item.actual)
            return false
          const delta = this.hos_delta_minutes(item.start_datetime, item.actual.actualClockInTime)
          return delta != null && Math.abs(delta) > this.hos_diff_threshold_minutes
        },
        hos_finish_diff_over_threshold(item) {
          if (!this.hos_diff_threshold_minutes || !item || item.status !== 'success' || !item.actual)
            return false
          const delta = this.hos_delta_minutes(item.finish_datetime, item.actual.actualClockOutTime)
          return delta != null && Math.abs(delta) > this.hos_diff_threshold_minutes
        },
        is_hos_diff_over_threshold(item) {
          return this.hos_start_diff_over_threshold(item) || this.hos_finish_diff_over_threshold(item)
        },
        recorded_hos_time(actual, actual_field, original_field, reason_field) {
          // what the driver's ELD recorded: the preserved original when processing
          // calculated a different time, and nothing at all when the calculated
          // time was never an HOS event (a flat shift, say)
          if (!actual)
            return null
          if (actual[original_field])
            return actual[original_field]
          return actual[reason_field] ? null : actual[actual_field]
        },
        driver_times_were_calculated(actual) {
          if (!actual)
            return false
          return !!(actual.actualClockInTimeOriginal || actual.actualClockInAdjustment
            || actual.actualClockOutTimeOriginal || actual.actualClockOutAdjustment)
        },
        adjusted_driver_time(actual, actual_field) {
          // both ends are listed together: a rule that shifted one could equally
          // have shifted the other, so an end that landed on the HOS time still
          // shows rather than going missing
          if (!this.driver_times_were_calculated(actual))
            return null
          return actual[actual_field]
        },
        hos_start_diff_highlight(item) {
          return this.selected_status === 'hos_diff_over_threshold' && this.hos_start_diff_over_threshold(item)
        },
        hos_finish_diff_highlight(item) {
          return this.selected_status === 'hos_diff_over_threshold' && this.hos_finish_diff_over_threshold(item)
        },
        hos_time_highlight_classes(item, side, overlapHighlight) {
          const classes = {}
          if (overlapHighlight)
            classes['overlap-time'] = true
          if (side === 'start' && this.hos_start_diff_highlight(item))
            classes['hos-diff-time'] = true
          if (side === 'finish' && this.hos_finish_diff_highlight(item))
            classes['hos-diff-time'] = true
          return classes
        },
        filter_by_status(status) {
          if (status == null || this.selected_status == status) {
            this.selected_status = null
          } else {
            this.selected_status = status
          }
          this.currentPage = 1
          this.filter_results()
        },
        on_feedback_saved(event) {
          const payload = event || {}
          const target = findActualsFeedbackRow(this.items_full, payload.item, true)
          if (target && target.actual) {
            if (payload.rating == null) {
              this.$delete(target.actual, 'feedback')
              this.$delete(target.actual, 'feedback_comment')
            } else {
              this.$set(target.actual, 'feedback', payload.rating)
              if (payload.rating === 'concern' && payload.comment)
                this.$set(target.actual, 'feedback_comment', payload.comment)
              else
                this.$delete(target.actual, 'feedback_comment')
            }
          }
          this.filter_results()
        },
        filter_by_feedback(feedback) {
          if (feedback == null || this.selected_feedback === feedback) {
            this.selected_feedback = null
          } else {
            this.selected_feedback = feedback
          }
          this.currentPage = 1
          this.filter_results()
        },
        filter_by_trip_duration(duration) {
          if (duration == null || this.selected_trip_duration === duration) {
            this.selected_trip_duration = null
          } else {
            this.selected_trip_duration = duration
          }
          this.currentPage = 1
          this.filter_results()
        },
        toggle_results_filters() {
          this.results_filters_open = !this.results_filters_open
        },
        filter_by_booking_type(bookingType) {
          if (bookingType == null || this.selected_booking_type === bookingType) {
            this.selected_booking_type = null
          } else {
            this.selected_booking_type = bookingType
          }
          this.currentPage = 1
          this.filter_results()
        },
        async confirm_clear_feedback_for_date_range() {
          if (this.clearing_feedback || !this.feedback_audit_mode_enabled)
            return
          const fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const toDate = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const ratedCount = this.feedback_positive_cnt + this.feedback_negative_cnt + this.feedback_concern_cnt
          const rangeLabel = `${this.details_fromDate} to ${this.details_toDate}`
          const msg = ratedCount > 0
            ? `Delete feedback on ${ratedCount} actuals from ${rangeLabel}?`
            : `Delete all actuals feedback from ${rangeLabel}?`
          if (!confirm(`${msg} This cannot be undone from the actuals page.`))
            return
          this.clearing_feedback = true
          try {
            const {value: {data = {}}} = await clear_tbn_actuals_feedback_for_date_range(this.user.tenant, fromDate, toDate)
            const deleted = data.deleted_count || 0
            this.selected_feedback = null
            this.clear_feedback_from_loaded_rows()
            this.$emit('showAlert', `Cleared feedback on ${deleted} actuals`, 'success', 4000)
            if (deleted > 0)
              await this.load_data()
          } catch (e) {
            console.warn('Failed to clear actuals feedback:', e)
            this.$emit('showAlert', 'error clearing feedback, try again or contact support', 'danger', 5000)
          } finally {
            this.clearing_feedback = false
          }
        },
        clear_feedback_from_loaded_rows() {
          for (const item of this.items_full || []) {
            if (item && item.actual) {
              this.$delete(item.actual, 'feedback')
              this.$delete(item.actual, 'feedback_comment')
            }
          }
          this.filter_results()
        },
        filter_results() {
          if (!this.show_overlap_filters
            && (this.selected_status === 'driver_overlap'
              || this.selected_status === 'vehicle_overlap'
              || this.selected_status === 'overlap_correction')) {
            this.selected_status = null
          }
          if (this.selected_booking_type != null && !this.show_booking_type_filters) {
            this.selected_booking_type = null
          }
          this.items = excludeSkippedUnlessSelected(this.items_full, this.selected_status)
          if (!this.items)
            return
          if (this.selected_status != null) {
            if (this.selected_status === 'driver_overlap') {
              this.items = this.build_overlap_display_items(
                this.items_full,
                'driver',
                'driver_overlap',
                'driver_overlap_group'
              )
            } else if (this.selected_status === 'vehicle_overlap') {
              this.items = this.build_overlap_display_items(
                  this.items_full,
                  'vehicle',
                  'vehicle_overlap',
                  'vehicle_overlap_group'
              )
            } else if (this.selected_status === 'hos_diff_over_threshold') {
              this.items = this.items.filter(item => this.is_hos_diff_over_threshold(item))
            } else if (this.selected_status === 'overlap_correction') {
              this.items = this.items.filter(item => hasOverlapCorrection(item))
            } else
              this.items = this.items.filter(item => item.status == this.selected_status)
          }
          const val = this.booking_filter
          if (val != '')

            if (this.booking_filter.indexOf("=") != -1) {
              const field = this.booking_filter.split("=")[0]
              const value = this.booking_filter.split("=")[1]
              if (field == 'driver')
                this.items = this.items.filter(item => item.driver == value)
              else if (field == 'vehicle')
                this.items = this.items.filter(item => item.vehicle == value)
            } else {
              this.items = this.items.filter(item => this.contains(item.ID, val) || this.contains(item.routeDescription, val) || this.contains(item.status_msg, val) || this.contains(item.driver, val) || this.contains(item.vehicle, val))
            }
          if (this.selected_driver) {
            this.items = this.items.filter(item => matchesActualsColumnFilter(item, this.selected_driver, {
              valueFn: driverColumnValue,
              overlapGroupType: 'driver',
            }))
          }
          if (this.selected_vehicle) {
            this.items = this.items.filter(item => matchesActualsColumnFilter(item, this.selected_vehicle, {
              valueFn: vehicleColumnValue,
              overlapGroupType: 'vehicle',
            }))
          }
          if (this.selected_base != '') {
            this.items = this.items.filter(item => this.is_overlap_section_header(item) || (item.startBase && item.startBase.indexOf(this.selected_base) != -1))
          }
          let navItems = this.items
          if (this.selected_trip_duration != null) {
            this.items = this.items.filter(item => matchesActualsTripDurationFilter(item, this.selected_trip_duration, true))
          }
          if (this.selected_booking_type != null) {
            this.items = this.items.filter(item => matchesActualsBookingTypeFilter(item, this.selected_booking_type, 'tbn'))
          }
          if (this.selected_feedback != null && this.feedback_audit_mode_enabled) {
            this.items = this.items.filter(item => matchesActualsFeedbackFilter(item, this.selected_feedback))
          }
          if (!this.show_overlap_group_headers) {
            this.items = this.strip_overlap_section_headers(this.items)
            this.hos_modal_nav_items = this.strip_overlap_section_headers(navItems)
          } else {
            this.hos_modal_nav_items = navItems
            this.schedule_overlap_header_colspans()
          }
        },
        // the finish time, same rule as the start (HOS off duty, falling back to vehicle-back time -- the mirror of the start pair)
        card_actual_finish(item) {
          if (!item || item._overlap_group_header)
            return null
          const actual = item.actual
          if (!actual)
            return null
          return actual.actualClockOutTime || actual.actualFinishLocationTime || null
        },
        // which card cells have nothing to show for this row, so the grid row they
        // were placed in can be dropped along with its icon
        card_cell_is_blank(key, item) {
          if (!item)
            return true
          if (item._overlap_group_header)
            return key !== 'card_ids'
          switch (key) {
            case 'card_ids': return !item.bookingID
            case 'card_time': return !(this.card_actual_start(item) || item.start_datetime)
            case 'card_finish': return !(this.card_actual_finish(item) || item.finish_datetime)
            case 'card_base': return !this.card_base_text(item)
            case 'card_route': return !this.card_route_text(item)
            case 'card_totals': return !this.card_totals_text(item)
            case 'card_feedback': return !this.card_has_actuals(item)
            case 'driver': return !item.driver
            case 'vehicle': return !item.vehicle && !item.vehicle_overlap
            default: return false
          }
        },
        card_base_text(item) {
          if (!item || item._overlap_group_header)
            return ''
          // start -> finish. TBN only fills startBase on day 1 and finishBase on the
          // final day, so a middle day of a multiday trip legitimately has neither
          const parts = [item.startBase, item.finishBase]
            .filter(Boolean)
            .filter((base, i, all) => i === 0 || base !== all[0])
          return parts.join(' \u2192 ')
        },
        card_route_text(item) {
          if (!item || item._overlap_group_header)
            return ''
          return item.routeDescription || ''
        },
        // a row has actuals once either end has been recorded -- the same pair of
        // timestamp checks the card's two time cells make
        // the flag is for judging the processor's verdict, so it shows on any row
        // it actually reached one on -- success or error alike
        card_has_actuals(item) {
          if (!item || item._overlap_group_header)
            return false
          return item.status === 'success' || item.status === 'error'
        },
        // recorded duration and distance, blank until there are actuals to report.
        // same null-guard idiom the status-line builder uses on these two fields.
        card_totals_text(item) {
          if (!item || item._overlap_group_header)
            return ''
          const actual = item.actual
          if (!actual)
            return ''
          const parts = []
          if (actual.actualTotalDuration != null && actual.actualTotalDuration !== '')
            parts.push((Number(actual.actualTotalDuration) / 60).toFixed(2) + ' hrs')
          if (actual.actualTotalDistance != null && actual.actualTotalDistance !== '')
            parts.push(actual.actualTotalDistance + ' mi')
          return parts.join(' \u00b7 ')
        },
        // the card's start time: the recorded actual when there is one, otherwise
        // the plan. `item.actual` is attached to EVERY row by the backend
        // (tbn_actuals_json.py:498) whatever the status, so it is never a test for
        // "has actuals" on its own -- only the timestamp keys are.
        card_actual_start(item) {
          if (!item || item._overlap_group_header)
            return null
          const actual = item.actual
          if (!actual)
            return null
          return actual.actualClockInTime || actual.actualStartLocationTime || null
        },
        // TBN has no dispatcher-facing movement id (vehicleAssignmentID and
        // dispatchID are admin-only columns), so the leg is named by trip day
        card_trip_day(item) {
          const final = parseInt(item && item.final_day_of_trip, 10)
          if (!(final > 1))
            return ''
          return 'Day ' + (item.day_of_trip || 1) + '/' + final
        },
        minutesToHours(minutes) {
          if (minutes == null || minutes === 0) return ''
          return '(' + (minutes / 60).toFixed(2) + 'hrs)'
        },
        contains(val1, val2) {
          if (val1 != null) {
            return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
          }
          return false;
        },
        non_success_status_msg_parts(item) {
          const html = item._status_msg_base || item.status_msg || ''
          if (!html)
            return []
          return html.split(',').map(part => part.trim()).filter(part => part.length > 0)
        },
        error_category_ids_for_item(item) {
          if (!item || item.status !== 'error')
            return []
          const ids = new Set()
          for (const rawPart of this.non_success_status_msg_parts(item)) {
            const text = String(rawPart).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            for (const sub of text.split('|').map(s => s.trim()).filter(s => s.length)) {
              ids.add(categorizeError(sub, 'tbn').id)
            }
          }
          return Array.from(ids)
        },
      },
      computed:{
        ...mapState({
          user: state => state.profile.user,
          viewAs: state => state.profile.view_as,
        }),
        overlap_updates_modal_updates() {
          return (this.overlap_updates_modal_item && this.overlap_updates_modal_item.overlap_updates) || []
        },
        overlap_updates_modal_booking_label() {
          return this.overlap_correction_booking_label(this.overlap_updates_modal_item)
        },
        isEffectiveAdmin() {
          return this.user && this.user.role === 'admin' && this.viewAs !== 'non-admin'
        },
        isSamsaraTenant() {
          const type = (this.user && this.user.telematics_types && this.user.telematics_types[this.user.tenant])
            || (this.summary && this.summary.telematics_type)
            || ''
          return type === 'SAMSARA' || type === ''
        },
        show_base_select() {
          return this.base_required || (this.base_options && this.base_options.length > 2)
        },
        // a hand-picked date leaves the grid on the previous window, so the Search
        // button carries the fact that the two no longer agree
        range_pending() {
          if (!this.loaded_fromDate)
            return false
          return this.details_fromDate !== this.loaded_fromDate
            || this.details_toDate !== this.loaded_toDate
        },
        // native date inputs speak ISO; the page stores MM-DD-YYYY
        from_date_iso: {
          get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
          set(v) {
            if (!v) return
            this.details_fromDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY')
          },
        },
        to_date_iso: {
          get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
          set(v) {
            if (!v) return
            this.details_toDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY')
          },
        },
        date_min_iso() {
          return moment(this.options.minDate).format('YYYY-MM-DD')
        },
        date_max_iso() {
          return moment(this.options.maxDate).format('YYYY-MM-DD')
        },
        // a window that is not one of the preset chips: the ... chip reads active
        // for it, so a bespoke range never looks like no selection at all
        is_custom_range() {
          return !this.quick_range_options.some(opt => opt.value === this.quick_range_active_days)
        },
        dates_open() {
          return this.show_custom_dates || this.is_custom_range
        },
        // the double arrows shift by the whole loaded window
        selected_day_count() {
          const days = this.quick_range_active_days
          return days > 0 ? days : 1
        },
        quick_range_active_days() {
          const from = moment(this.details_fromDate, 'MM-DD-YYYY')
          const to = moment(this.details_toDate, 'MM-DD-YYYY')
          return to.diff(from, 'days') + 1
        },
        // the chips are the only place the loaded dates are shown now
        // below the card breakpoint the table stacks into one card per row, so the
        // column list is cut to what fits a phone -- everything else stays reachable
        // through the row's details toggle, which stacking keeps working
        // card order, not table order: the head line is toggle / id / start / finish,
        // then driver and vehicle, then the status message and any row actions
        card_field_keys() {
          return ['card_ids', 'card_time', 'card_finish', 'card_totals', 'card_feedback', 'driver', 'vehicle', 'card_base', 'card_route']
        },
        // the role decides where a cell lands in the card grid, so one stylesheet
        // serves both pages even though they name their columns differently
        card_cell_roles() {
          return {
            card_ids: 'title',
            card_time: 'start',
            card_finish: 'finish',
            card_totals: 'totals',
            card_feedback: 'feedback',
            driver: 'driver',
            vehicle: 'vehicle',
            card_base: 'base',
            card_route: 'route',
          }
        },
        table_fields() {
          let fields = this.fields
          if (this.cardView) {
            const byKey = new Map(fields.map(field => [field.key, field]))
            fields = this.card_field_keys.map(key => byKey.get(key) || { key, label: '' })
          }
          if (!this.isEffectiveAdmin)
            fields = fields.filter(field => field.key !== 'processed_time' && field.key !== 'last_updated')
          return fields.map(field => ({
            ...field,
            tdClass: (value, key, item) => {
              const classes = [this.get_overlap_td_class(field.key, item)]
              if (this.cardView) {
                classes.push('actuals-card-cell--' + (this.card_cell_roles[field.key] || 'wide'))
                if (this.card_cell_is_blank(field.key, item))
                  classes.push('actuals-card-cell--blank')
              }
              return classes.filter(Boolean).join(' ')
            },
          }))
        },
        calc_total_duration() {
          const total_minutes = (this.items || []).reduce((accumulator, current) => accumulator + (current.total_duration || 0), 0)
          return (total_minutes / 60).toFixed(2)
        },
        success_cnt() {
          return this.items_full.filter(item => item.status=='success').length
        },
        error_cnt() {
          return this.items_full.filter(item => item.status=='error').length
        },
        cancelled_cnt() {
          return this.items_full.filter(item => item.status=='cancelled').length
        },
        unprocessed_cnt() {
          return this.items_full.filter(item => item.status=='unprocessed').length
        },
        skipped_cnt() {
          return this.items_full.filter(item => item.status=='skipped').length
        },
        driver_overlap_cnt() {
          return this.items_full.filter(item => item.driver_overlap).length
        },
        vehicle_overlap_cnt() {
          return this.items_full.filter(item => item.vehicle_overlap).length
        },
        overlap_correction_cnt() {
          return this.items_full.filter(item => hasOverlapCorrection(item)).length
        },
        show_overlap_filters() {
          return this.driver_overlap_cnt > 0 || this.vehicle_overlap_cnt > 0 || this.overlap_correction_cnt > 0
        },
        hos_modal_nav() {
          return hosModalNavState(this.hos_modal_nav_items, this.hos_modal_item, true)
        },
        hos_modal_nav_has_prev() {
          return this.hos_modal_nav.hasPrev
        },
        hos_modal_nav_has_next() {
          return this.hos_modal_nav.hasNext
        },
        hos_modal_nav_label() {
          return this.hos_modal_nav.label
        },
        hos_diff_over_threshold_cnt() {
          return this.items_full.filter(item => this.is_hos_diff_over_threshold(item)).length
        },
        items_for_feedback_counts() {
          return filterActualsItemsByStatus(this.items_full, this.selected_status, {
            isHosDiffOverThreshold: item => this.is_hos_diff_over_threshold(item),
          })
        },
        items_for_trip_duration_counts() {
          return this.items_for_feedback_counts
        },
        trip_single_cnt() {
          return countActualsTripDuration(this.items_for_trip_duration_counts, 'single', true)
        },
        trip_multiday_cnt() {
          return countActualsTripDuration(this.items_for_trip_duration_counts, 'multiday', true)
        },
        booking_type_filter_options() {
          return actualsBookingTypeFilters('tbn')
            .map(opt => ({...opt, count: countActualsBookingType(this.items_for_trip_duration_counts, opt.value, 'tbn')}))
            .filter(opt => opt.count > 0)
        },
        active_filter_count() {
          let n = 0
          if (this.selected_status != null) n++
          if (this.selected_trip_duration != null) n++
          if (this.selected_booking_type != null) n++
          if (this.booking_filter) n++
          if (this.selected_driver) n++
          if (this.selected_vehicle) n++
          if (this.selected_base) n++
          if (this.selected_feedback != null && this.feedback_audit_mode_enabled) n++
          return n
        },
        show_booking_type_filters() {
          return this.booking_type_filter_options.length > 1
        },
        feedback_positive_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'positive')
        },
        feedback_negative_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'negative')
        },
        feedback_concern_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'concern')
        },
        feedback_none_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'none')
        },
        feedback_audit_mode_enabled() {
          return isFeedbackAuditModeEnabled(this.user)
        },
        calc_total_miles() {
          return (this.items || []).reduce((accumulator, current) => accumulator + (current.total_miles || 0), 0)
        },
        calc_total_odo_miles() {
          return (this.items || []).reduce((accumulator, current) => accumulator + (current.total_odo_miles || 0), 0)
        },
        error_summary_troubleshooter_link() {
          return {
            path: '/actuals_troubleshooter',
            query: {
              source: 'tbn',
              fromDate: this.details_fromDate,
              toDate: this.details_toDate,
              filter: this.actuals_filter,
              base: this.selected_base,
            },
          }
        },
        show_overlap_group_headers() {
          return this.selected_status === 'driver_overlap' || this.selected_status === 'vehicle_overlap'
        },
        actuals_table_key() {
          return (this.cardView ? 'cards-' : '') + (this.selected_status || 'all')
        },
        actuals_sticky_header() {
          if (this.cardView)
            return false
          return this.items_row_count > 0 ? '70vh' : false
        },
        items_row_count() {
          return (this.items || []).filter(item => !item._overlap_group_header).length
        },
        driver_filter_options() {
          return buildDistinctColumnFilterOptions(this.items_full, driverColumnValue, { allLabel: 'All drivers' })
        },
        vehicle_filter_options() {
          return buildDistinctColumnFilterOptions(this.items_full, vehicleColumnValue, { allLabel: 'All vehicles' })
        },
      },
      updated() {
        this.schedule_overlap_header_colspans()
      },
      beforeDestroy() {
        this.stop_overlap_colspan_observer()
        if (this.copied_booking_id_timer)
          clearTimeout(this.copied_booking_id_timer)
      },
      watch: {
        user: {
          deep: true,
          async handler() {
            this.resetTenantBases()
            await this.load_bases()
            this.load_data()
          }
        },
        viewAs() {
          this.check_features()
        },
        details_toDate() {
          if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
            this.details_toDate = this.details_fromDate
          else
            this.applyLargeFleetDateRange('to')
        },
        details_fromDate() {
          if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
            this.details_toDate = this.details_fromDate
          else
            this.applyLargeFleetDateRange('from')
        },
        selected_base() {
          this.filter_results()
        },
        hos_diff_threshold_minutes(val) {
          // The dropdown is the filter: a threshold applies it, empty selection clears it.
          if (val) {
            this.selected_status = 'hos_diff_over_threshold'
          } else if (this.selected_status === 'hos_diff_over_threshold') {
            this.selected_status = null
          }
          this.currentPage = 1
          this.filter_results()
        },
        booking_filter() {
          this.filter_results()
        },
        selected_driver() {
          this.currentPage = 1
          this.filter_results()
        },
        selected_vehicle() {
          this.currentPage = 1
          this.filter_results()
        },
        currentPage() {
          this.schedule_overlap_header_colspans()
        },
        fields() {
          this.schedule_overlap_header_colspans()
        },
        items() {
          this.schedule_overlap_header_colspans()
        },
        show_overlap_group_headers(active) {
          if (active)
            this.start_overlap_colspan_observer()
          else
            this.stop_overlap_colspan_observer()
        },
        actuals_table_key() {
          if (this.show_overlap_group_headers)
            this.start_overlap_colspan_observer()
        },
      }
    }
</script>

<style>

.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.skipped { background-color: hsla(190,90%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }
.overlap-driver { background-color: hsla(210,85%,75%,0.45) }
.overlap-vehicle { background-color: hsla(35,100%,70%,0.45) }
.overlap-both { background-color: hsla(270,55%,78%,0.45) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.skipped td { background-color: hsla(190,90%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }
.overlap-driver td { background-color: hsla(210,85%,75%,0.45) }
.overlap-vehicle td { background-color: hsla(35,100%,70%,0.45) }
.overlap-both td { background-color: hsla(270,55%,78%,0.45) }
.overlap-badge {
  color: #b45309;
  font-weight: bold;
  margin-right: 2px;
}
.overlap-resource {
  font-weight: bold;
  text-decoration: underline;
  text-decoration-color: #b45309;
}
.actuals-missing-driver {
  color: #dc3545;
  font-style: italic;
}
.actuals-telematics-link {
  font-weight: bold;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: #b45309;
}
.actuals-telematics-link:hover {
  color: var(--fi-action-primary);
  text-decoration-color: var(--fi-action-primary);
}
.overlap-time {
  font-weight: bold;
  color: #c0392b;
}
.hos-diff-time {
  font-weight: bold;
  color: #c0392b;
}
.actuals-details-icon-btn {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #3182bd;
  font-size: 22px;
  line-height: 1;
  width: 32px;
  height: 32px;
  cursor: pointer;
}
.actuals-details-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.actuals-details-icon-btn:hover:not(:disabled) {
  color: #0c3d5c;
}
.actuals-tbn-table td.actuals-details-td {
  vertical-align: middle;
  text-align: center;
}
.actuals-details-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}
.actuals-details-cell--stacked {
  flex-direction: column;
  gap: 2px;
}
.actuals-id-cell {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}
.actuals-id-copy {
  white-space: nowrap;
  font-weight: bold;
  flex-shrink: 0;
  line-height: 1.35;
}
.actuals-id-text {
  font-size: 0.75em;
  font-weight: normal;
  min-width: 0;
  flex: 1;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}
.actuals-id-link {
  color: inherit;
  text-decoration: none;
}
.actuals-id-link:hover {
  color: var(--fi-action-primary);
  text-decoration: underline;
}
.actuals-tbn-table td.actuals-id-td {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
  white-space: normal;
  vertical-align: top;
}
.actuals-tbn-table tbody tr.overlap-group-header-row {
  background-color: #f8f9fa !important;
}
.actuals-tbn-table tbody tr.overlap-group-header-row td.overlap-group-header-empty {
  display: none;
}
.actuals-tbn-table tbody tr.overlap-group-header-row td.overlap-group-header-cell {
  padding: 0 !important;
  border-top: 3px solid #6c757d !important;
  border-bottom: 1px solid #dee2e6 !important;
  background: transparent !important;
}
.overlap-section-heading {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}
.overlap-section-heading--driver {
  background-color: hsla(210, 85%, 75%, 0.55);
  border-left: 5px solid #3182bd;
  color: #0c3d5c;
}
.overlap-section-heading--vehicle {
  background-color: hsla(35, 100%, 70%, 0.55);
  border-left: 5px solid #e6a23c;
  color: #7c4a03;
}

div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

.actuals-cm-status-filter-row {
  position: relative;
  width: 100%;
}
.actuals-error-filter-group {
  position: absolute;
  right: 0;
  top: 0;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-weight: normal;
  flex-shrink: 0;
  z-index: 1;
}
.actuals-error-filter-label {
  font-weight: 600;
  margin-right: 8px;
  color: #4a6785;
}
.actuals-error-filter-select {
  display: inline-block;
  width: auto;
  min-width: 280px;
  vertical-align: middle;
}
.actuals-error-filter-clear {
  margin-left: 6px;
  padding: 0 6px;
}
.actuals-booking-filter-input {
  width: 150px;
  font-size: 13px;
  font-weight: normal;
}
.actuals-resource-filter-select {
  width: 140px;
  font-size: 13px;
  font-weight: normal;
}

.actuals-filter-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 8px 10px;
  font-size: 16px;
  font-weight: bold;
}
.actuals-filter-area {
  margin-bottom: 0;
  width: 100%;
}
.actuals-totals-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.actuals-totals-group .actuals-filter-label,
.actuals-totals-group .actuals-chip {
  font-size: 13px;
  font-weight: 600;
}
.actuals-totals-group .actuals-chip {
  padding: 2px 8px;
}
.actuals-feedback-filter-group {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.actuals-trip-duration-filter-group,
.actuals-feedback-filter-group .actuals-chip,
.actuals-trip-duration-filter-group .actuals-filter-label,
.actuals-trip-duration-filter-group .actuals-chip,
.actuals-booking-type-filter-group .actuals-filter-label,
.actuals-booking-type-filter-group .actuals-chip {
  font-size: 13px;
  font-weight: 600;
}
.actuals-feedback-filter-group .actuals-chip,
.actuals-trip-duration-filter-group .actuals-chip,
.actuals-booking-type-filter-group .actuals-chip {
  padding: 2px 8px;
}
.filter-expander {
  cursor: pointer;
  color: #6c757d;
  padding: 0 5px;
  user-select: none;
  font-size: 16px;
  line-height: 1;
}
.filter-expander:hover {
  color: #343a40;
}
.actuals-filter-label {
  margin-right: 4px;
  white-space: nowrap;
  color: #6c757d;
  font-weight: 600;
  font-size: 14px;
}
.actuals-filter-sep {
  color: #adb5bd;
  user-select: none;
  font-size: 16px;
}
.actuals-chip {
  display: inline-block;
  border: 1px solid #ced4da;
  background-color: #f8f9fa;
  color: #212529;
  border-radius: 999px;
  padding: 4px 12px;
  white-space: nowrap;
  line-height: 1.3;
  font-size: 16px;
}
.actuals-chip-clickable {
  cursor: pointer;
}
.actuals-chip-clickable:hover {
  filter: brightness(0.97);
}
.actuals-chip-selected:not(.actuals-chip-total) {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 3px 11px;
}
.actuals-chip-total.actuals-chip-selected {
  border: 1px solid #ced4da !important;
  box-shadow: none !important;
  padding: 3px 11px;
}
.actuals-chip.success { background-color: hsla(120,100%,75%,0.55); border-color: #7dcc7d; }
.actuals-chip.error { background-color: rgba(255,0,0,0.2); border-color: #e88; }
.actuals-chip.unprocessed { background-color: hsla(200,40%,80%,0.55); border-color: #9bb; }
.actuals-chip.skipped { background-color: hsla(190,90%,80%,0.55); border-color: #8cc; }
.actuals-chip.cancelled { background-color: hsla(290,60%,70%,0.45); border-color: #b9b; }
.actuals-chip.overlap-driver { background-color: hsla(210,85%,75%,0.65); border-color: #6baed6; color: #0c3d5c; }
.actuals-chip.overlap-vehicle { background-color: hsla(35,100%,70%,0.65); border-color: #e6a23c; color: #7c4a03; }
.actuals-chip.overlap-correction { background-color: hsla(211, 100%, 93%, 0.95); border-color: #0d6efd; color: #0a58ca; }
.actuals-chip.hos-diff { background-color: hsla(45,100%,80%,0.65); border-color: #d4a017; color: #6b4f00; }
.actuals-chip.feedback-positive { background-color: hsla(120,100%,75%,0.45); border-color: #198754; color: #0f5132; }
.actuals-chip.feedback-negative { background-color: rgba(255,0,0,0.15); border-color: #dc3545; color: #842029; }
.actuals-chip.feedback-concern { background-color: hsla(45,100%,90%,0.55); border-color: #b58100; color: #664d03; }
.actuals-chip.feedback-none { background-color: #f1f3f5; border-color: #adb5bd; color: #495057; }
/* Status / overlap chips use the same compact sizing as the Trip, Type and Feedback chips. */
.actuals-tbn-filter-chips .actuals-chip {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 8px;
}
.actuals-tbn-filter-chips .actuals-chip-selected:not(.actuals-chip-total) {
  padding: 1px 7px;
}
.actuals-tbn-filter-chips .actuals-chip-total.actuals-chip-selected {
  padding: 2px 8px;
}
.actuals-tbn-filter-chips .filter-expander {
  font-size: 14px;
}
.actuals-feedback-chip i {
  margin-right: 4px;
}
.actuals-tbn-hos-diff-threshold {
  width: auto;
  max-width: 100px;
  min-width: 0;
  flex: 0 0 auto;
  font-size: 13px !important;
  font-weight: 600;
  padding: 2px 1.25rem 2px 6px !important;
  height: auto !important;
  line-height: 1.25;
  background-position: right 0.35rem center;
  background-size: 8px 10px;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

.actuals-page-root .actuals-tbn-toolbar {
  /* every control in the row is pinned to this. `height: auto` is not enough for
     <select>: it keeps a UA minimum content height that ignores the 12.5px
     line-height the chips and inputs shrink to, so the two dropdowns sat taller
     than everything beside them. */
  --actuals-toolbar-control-h: 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px 10px;
  font-size: 16px;
  font-weight: bold;
  margin: 16px 0 12px;
  position: relative;
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-sep {
  color: #adb5bd;
  font-size: 14px;
  user-select: none;
}
/* selects, inputs and buttons carry the date chips' type scale and box height so
   the toolbar reads as one row rather than two sizes of control side by side */
.actuals-page-root .actuals-tbn-toolbar .toolbar-date,
.actuals-page-root .actuals-tbn-toolbar .toolbar-select,
.actuals-page-root .actuals-tbn-toolbar .toolbar-input,
.actuals-page-root .actuals-tbn-toolbar .toolbar-btn,
.actuals-page-root .actuals-tbn-toolbar .toolbar-label {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.5;
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-date,
.actuals-page-root .actuals-tbn-toolbar .toolbar-select,
.actuals-page-root .actuals-tbn-toolbar .toolbar-input,
.actuals-page-root .actuals-tbn-toolbar .toolbar-btn {
  height: var(--actuals-toolbar-control-h, 28px);
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-date,
.actuals-page-root .actuals-tbn-toolbar .toolbar-input {
  padding: 0 10px;
}
/* the right-hand padding is the caret's gutter -- collapsing it to match the
   inputs would run the option text under the arrow. bootstrap sizes that gutter
   for 16px type, so it is pulled in with the caret to match the 12.5px text */
.actuals-page-root .actuals-tbn-toolbar .toolbar-select {
  padding: 0 1.4rem 0 8px;
  background-position: right 0.45rem center;
  background-size: 8px 10px;
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: var(--fi-radius-pill, 999px);
  flex-shrink: 0;
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-date { width: 106px; }
.actuals-page-root .actuals-tbn-toolbar .toolbar-select { width: 134px; }
.actuals-page-root .actuals-tbn-toolbar .toolbar-base { width: 144px; }
.actuals-page-root .actuals-tbn-toolbar .toolbar-input { width: 108px; }
.actuals-page-root .actuals-tbn-toolbar .toolbar-label {
  white-space: nowrap;
  color: #6c757d;
}

@media (max-width: 768px) {
  /* the separator divided the dates from controls that are behind a button now */
  .actuals-page-root .actuals-tbn-toolbar .toolbar-sep {
    display: none;
  }
  .actuals-page-root .actuals-tbn-toolbar .actuals-date-presets {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}
.actuals-page-root .actuals-tbn-toolbar .toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 6px 8px;
  flex-shrink: 0;
}

</style>

